import { faChartLineUp, faCheckCircle, faChevronCircleRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PageProps } from "gatsby";
import React, { useEffect, useState } from "react";
import { z } from "zod";
import { Bullet } from "..";
import { COUNTRY_FOR_LOCALE, LANGUAGE_FOR_LOCALE, Locale } from "../../../config";
import { Container } from "../../shared/Container2";
import { Img } from "../../shared/Img";
import { languageSwitch } from "../../shared/Languages";
import { LinkAsButton } from "../../shared/LinkAsButton";
import { Section } from "../../shared/Section";
import { TrackedExternalLink, TrackedLink } from "../../shared/TrackedLink";
import { ENDORSEMENTS } from "../../shared/endorsements/Endorsement";
import { useTracking } from "../../shared/tracking/trackUser";
import { useClarity } from "../../shared/tracking/useClarity";
import { useGoogleTag } from "../../shared/tracking/useGoogleTag";
import { useLinkedIn } from "../../shared/tracking/useLinkedInTag";
import { useMetaPixel } from "../../shared/tracking/useMetaPixel";
import { Waitlist } from "./Waitlist";
import { OnboardingFee, PRICING } from "./prices";

// Greece
// Turkey
// Mexico
// Czech Republic
// Belgien (FR + NL part)
// Netherlands

export const SUPPORTED_EXPANSION_LOCALES = [
    "EL-GR", // - done
    "TR-TR", // - done
    "ES-MX", // - done
    "CZ-CZ", // - tbd
    // "FR-BE", // - tbd
    // "NL-BE", // - tbd
    "ET-EE", // - done
    "LV-LV", // - done
    "LT-LT", // - done
    "FR-CA", // - done

    "EN-CA", // - done
    "EN-GB", // tbd -> only for translation source
] satisfies Locale[];

export type SupportedExpansionLocale = Exclude<(typeof SUPPORTED_EXPANSION_LOCALES)[number], "EN-GB">;
export type NewSupportedExpansionLocale = Exclude<(typeof SUPPORTED_EXPANSION_LOCALES)[number], "EN-GB" | "CZ-CZ">;

export function isExpansionLocale(locale: Locale): locale is NewSupportedExpansionLocale {
    return (["EL-GR", "TR-TR", "ES-MX", "CZ-CZ", "ET-EE", "LV-LV", "LT-LT"] satisfies Locale[] as Locale[]).includes(
        locale,
    );
}

export type SupportedLocale = (typeof SUPPORTED_EXPANSION_LOCALES)[number];

const ABTestPriceVariants = ["a", "b", "c", "d", "e", "f"] as const;
type ABTestPriceVariant = (typeof ABTestPriceVariants)[number];

export type ABTestVariants = {
    price: ABTestPriceVariant;
    onboardingFee: ABTestPriceVariant;
};

const getRandom = <T extends string>(arr: readonly T[]): T => {
    const max = arr.length;
    const index = Math.min(Math.floor(Math.random() * max), max - 1);
    return arr[index];
};

const LSID_P = "88ac6006-62fe-4863-9887-4ee87927d652";
const LSID_S = "e44c6edc-9d51-44a0-b162-cb2166d0974e";

export const CountryTestLandingPage = (props: PageProps & { locale: SupportedLocale }) => {
    const [variants, setVariants] = useState<{
        price: ABTestPriceVariant;
        onboardingFee: ABTestPriceVariant;
    }>();

    const validationObject = z.object({
        price: z.custom<ABTestPriceVariant>(),
        onboardingFee: z.custom<ABTestPriceVariant>(),
    });

    const { track, hasConsent, setConsent } = useTracking();

    useClarity(hasConsent);
    useGoogleTag(hasConsent);
    useMetaPixel(hasConsent, COUNTRY_FOR_LOCALE[props.locale]);
    useLinkedIn(hasConsent);

    useEffect(() => {
        const overridePrice = new URLSearchParams(props.location.search).get("price") as ABTestPriceVariant;
        const overrideOnboardingFee = new URLSearchParams(props.location.search).get(
            "onboarding",
        ) as ABTestPriceVariant;

        setConsent(true);

        const rawPrice = localStorage.getItem(LSID_P);
        const rawOnboarding = localStorage.getItem(LSID_S);

        const parsedLsDataPrice: undefined | string = !!rawPrice && rawPrice !== "undefined" ? rawPrice : undefined;
        const parsedLsDataOnboarding: undefined | string =
            !!rawOnboarding && rawOnboarding !== "undefined" ? rawOnboarding : undefined;

        const price = overridePrice || parsedLsDataPrice || getRandom(ABTestPriceVariants);
        const onboardingFee = overrideOnboardingFee || parsedLsDataOnboarding || getRandom(ABTestPriceVariants);

        const _variants: ABTestVariants = { price: price, onboardingFee: onboardingFee };
        const res = validationObject.safeParse(_variants);

        // only fallback, should never happen
        if (!res.success) {
            const state: ABTestVariants = {
                price: "a",
                onboardingFee: "a",
            };

            localStorage.setItem(LSID_P, state.price);
            localStorage.setItem(LSID_S, state.onboardingFee);
            setVariants(state);
        } else {
            localStorage.setItem(LSID_P, res.data.price);
            localStorage.setItem(LSID_S, res.data.onboardingFee);
            setVariants(res.data);
        }

        const _prices = makePriceMap({
            locale: props.locale,
            variants: res.data ?? { onboardingFee: "a", price: "a" },
        });

        // track page view here, so that variants can be included
        track({
            name: window.location.pathname,
            page: window.location.pathname,
            type: "Page",
            properties: {
                ...res.data,
                selectedPricing: _prices,
            },
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!variants) return <></>;
    return <CountryTestLandingPageEntry {...props} variants={variants} />;
};

const CountryTestLandingPageEntry = (props: PageProps & { locale: SupportedLocale; variants: ABTestVariants }) => {
    const lang = LANGUAGE_FOR_LOCALE[props.locale];

    const NavbarItem = ({ text, to }: { text: string; to: string }) => {
        return (
            <>
                <TrackedLink to={to} className="whitespace-nowrap relative group">
                    <div className="h-0 invisible font-bold">{text}</div>
                    <div className="text-gray-700 font-medium hover:text-black transition-all duration-200 hover:font-bold">
                        {text}
                    </div>
                </TrackedLink>
            </>
        );
    };

    return (
        <>
            {/* Hero */}
            <div className="backdrop-blur-lg bg-white/75 border-b sticky top-0 z-[100]">
                <div className="mx-auto w-full max-w-[1300px] flex items-center px-10 h-16 gap-5">
                    <Img size="lg" className="w-[74px]" alt="reonic logo" src={"/images/logo.svg"} />
                    <div className="w-full" />

                    <div className="-ml-8 md:hidden">
                        <LinkAsButton to="#waitlist" className={"bg-zinc-800 text-white hover:bg-zinc-700"}>
                            {languageSwitch(lang, {
                                // Login
                                ET: "Liitu ootenimekirjaga",
                                LT: "Prisijunkite prie laukiančiųjų sąrašo",
                                LV: "Pievienojies gaidīšanas sarakstam",
                                ES: "Inscribirse en la lista de espera",
                                TR: "Bekleme listesine katılın",
                                CZ: "Připojte se k čekací listině",
                                EL: "Εγγραφείτε στη λίστα αναμονής",
                                FR: "S'inscrire sur la liste d'attente",
                                NL: "Schrijf je in op de wachtlijst",
                                EN: "Join the waitlist",
                            })}
                        </LinkAsButton>
                    </div>

                    <div className="hidden md:flex w-full gap-5">
                        <NavbarItem
                            to="#why-reonic"
                            text={languageSwitch(lang, {
                                ET: "Miks Reonic",
                                LT: "Kodėl Reonic",
                                LV: "Kāpēc Reonic",
                                ES: "Por qué elegir Reonic",
                                TR: "Neden Reonic",
                                CZ: "Proč Reonic",
                                EL: "Γιατί να επιλέξετε το Reonic",
                                FR: "Pourquoi Reonic",
                                NL: "Waarom Reonic",
                                EN: "Why Reonic",
                            })}
                        />
                        <NavbarItem
                            to="#functionality"
                            text={languageSwitch(lang, {
                                ET: "Funktsioon",
                                LT: "Funkcija",
                                LV: "Funkcija",
                                ES: "Características",
                                TR: "Özellik",
                                CZ: "Funkce",
                                EL: "Χαρακτηριστικά",
                                FR: "Fonctionnalité",
                                NL: "Functies",
                                EN: "Features",
                            })}
                        />
                        <NavbarItem
                            to="#prices"
                            text={languageSwitch(lang, {
                                ET: "Hinnakujundus",
                                LT: "Kainodara",
                                LV: "Cenu noteikšana",
                                ES: "Precios",
                                TR: "Fiyatlandırma",
                                CZ: "Stanovení cen",
                                EL: "Τιμολόγηση",
                                FR: "Tarification",
                                NL: "Prijzen",
                                EN: "Pricing",
                            })}
                        />
                        <NavbarItem
                            to="#waitlist"
                            text={languageSwitch(lang, {
                                // Join the waitlist
                                ET: "Liitu ootenimekirjaga",
                                LT: "Prisijunkite prie laukiančiųjų sąrašo",
                                LV: "Pievienojies gaidīšanas sarakstam",
                                ES: "Inscribirse en la lista de espera",
                                TR: "Bekleme listesine katılın",
                                CZ: "Připojte se k čekací listině",
                                EL: "Εγγραφείτε στη λίστα αναμονής",
                                FR: "S'inscrire sur la liste d'attente",
                                NL: "Schrijf je in op de wachtlijst",
                                EN: "Join the waitlist",
                            })}
                        />
                    </div>

                    {/* 
                        Hide for now, people think they can sign up here/create an account
                    */}
                    {/* <div className="ml-2 hidden md:block">
                        <LinkAsButton
                            to={"https://portal.reonic.de"}
                            className={"bg-zinc-800 text-white hover:bg-zinc-700"}
                        >
                            {languageSwitch(lang, {
                                // Login
                                ET: "Logi sisse",
                                LT: "Prisijungti",
                                LV: "Pieteikšanās",
                                ES: "Inicio de sesión",
                                TR: "Giriş",
                                CZ: "Přihlášení",
                                EL: "Σύνδεση",
                                FR: "Connexion",
                                NL: "Inloggen",
                                EN: "Login",
                            })}
                        </LinkAsButton>
                    </div> */}
                </div>
            </div>
            <Section
                className="md:pt-60 pt-12 pb-44 grid md:grid-cols-[400px_1fr] lg:grid-cols-[525px_1fr] items-center gap-2 md:gap-6 "
                outerClassName="relative"
                relative={false}
            >
                <div className="text-d-5xl order-1">
                    {/* <div className="banner mb-7 scale-90 hidden md:flex">
                        <FlagForLocale locale={props.locale} mode="country" className="w-6 mx-2" />
                        {genericSwitch(props.locale, {
                            "ES-MX": "Próximamente en México, el 1 de febrero de 2025",
                            "TR-TR": "Türkiye'ye geliyor, 1 Şubat 2025",
                            "CZ-CZ": "Coming to Czech-Republic, 1st of February 2025",
                            "EL-GR": "Coming to Greece, 1st of February 2025",
                            "FR-BE": "Coming to Belgium, 1st of February 2025",
                            "NL-BE": "Coming to Belgium, 1st of February 2025",
                            "NL-NL": "Coming to the Netherlands, 1st of February 2025",
                            //
                            "ET-EE": "Launching in Estonia, 1st of February 2025",
                            "LV-LV": "Launching in Latvia, 1st of February 2025",
                            "LT-LT": "Launching in Lithuania, 1st of February 2025",
                            "EN-GB": "Launching in Mexico 1st of February 2025",
                        })}
                    </div> */}

                    <p className="font-bold">
                        {languageSwitch(lang, {
                            // The best software
                            ET: "Parim tarkvara fotogalvaaniliste süsteemide müümiseks ja paigaldamiseks.",
                            LT: "Geriausia programinė įranga fotovoltinėms sistemoms parduoti ir įrengti.",
                            LV: "Labākā programmatūra fotogalvanisko sistēmu pārdošanai un uzstādīšanai.",
                            ES: "El mejor software para vender e instalar sistemas fotovoltaicos.",
                            TR: "Fotovoltaik sistemleri satmak ve kurmak için en iyi yazılım.",
                            CZ: "Nejlepší software pro prodej a instalaci fotovoltaických systémů.",
                            EL: "Το καλύτερο λογισμικό για την πώληση και εγκατάσταση φωτοβολταϊκών συστημάτων.",
                            FR: "Le logiciel tout-en-un pour vendre et installer des systèmes photovoltaïques.",
                            NL: "De beste software voor de verkoop en installatie van PV-systemen.",
                            EN: "The best software for selling and installing PV Systems",
                        })}
                    </p>
                    {/* <div className=" w-[400px] my-5" /> */}
                    <p className="text-[13px] text-wrap mt-4 leading-[18px] text-gray-500">
                        {languageSwitch(lang, {
                            // From first customer contact to module placement, string planning, digital signatures, through to installation preparation, realisation and documentation. Including project management, e-mail and calendar integration. All in one.
                            ET: "Alates esimesest kliendikontaktist kuni moodulite paigutamise, stringide planeerimise, digitaalallkirjade, paigalduse ettevalmistamise, realiseerimise ja dokumenteerimiseni. Sealhulgas projektijuhtimine, e-posti ja kalendri integreerimine. Kõik ühes.",
                            LT: "Nuo pirmojo kontakto su klientu iki modulių išdėstymo, stygų planavimo, skaitmeninių parašų, montavimo paruošimo, realizavimo ir dokumentacijos. Įskaitant projektų valdymą, el. pašto ir kalendoriaus integraciją. Viskas viename.",
                            LV: "No pirmā kontakta ar klientu līdz moduļu izvietošanai, virkņu plānošanai, digitālajiem parakstiem, instalācijas sagatavošanai, realizācijai un dokumentācijai. Tostarp projektu vadība, e-pasta un kalendāra integrācija. Viss vienā.",
                            ES: "Desde el primer contacto con el cliente hasta la colocación de módulos, la planificación eléctrica, las firmas digitales, la preparación de la instalación, la realización y la documentación. Incluida la gestión de proyectos y la integración de correo electrónico y calendario. Todo en uno.",
                            TR: "İlk müşteri temasından modül yerleştirmeye, dizi planlamasına, dijital imzalara, kurulum hazırlığına, gerçekleştirmeye ve belgelendirmeye kadar. Proje yönetimi, e-posta ve takvim entegrasyonu dahil. Hepsi bir arada.",
                            CZ: "Od prvního kontaktu se zákazníkem přes umístění modulu, plánování řetězce, digitální podpisy až po přípravu instalace, realizaci a dokumentaci. Včetně řízení projektu, e-mailu a integrace kalendáře. Vše v jednom.",
                            EL: "Καλύπτει απόλυτα τις ανάγκες σας από την πρώτη επαφή με τον πελάτη έως την τοποθέτηση των μονάδων, την ηλεκτρολογική μελέτη, τις ψηφιακές υπογραφές, την προετοιμασία της εγκατάστασης, την υλοποίηση και την τεκμηρίωση. Με ενσωματωμένες εφαρμογές διαχείρισης έργου, ηλεκτρονικού ταχυδρομείου και ημερολογίου. Όλα σε ένα.",
                            FR: "De la première prise de contact avec le client jusqu’à l’installation et sa documentation, en passant par le calepinage, la planification des chaînes et la signature numérique. Le tout intégré à votre gestion de projet, votre messagerie et votre calendrier.",
                            NL: "Van het eerste klantcontact tot de plaatsing van de modules, de stringplanning, de digitale handtekeningen, de installatievoorbereiding, de uitvoering en de documentatie. Inclusief projectbeheer, e-mail- en agenda-integratie. Alles in één.",
                            EN: "Covering first customer contact, module placement, electrical planning, digital signatures, installation preparation, implementation and documentation. Including project management, email and calendar integration. All in one.",
                        })}
                    </p>

                    <div className="mt-5 md:mb-24 md:10 hidden md:block">
                        <LinkAsButton
                            to="#waitlist"
                            children={languageSwitch(lang, {
                                // Join the early-access waitlist
                                ET: "Liitu varajase juurdepääsu ootenimekirjaga",
                                LT: "Prisijunkite prie ankstyvosios prieigos laukiančiųjų sąrašo",
                                LV: "Pievienojies agrīnās piekļuves gaidīšanas sarakstam",
                                ES: "Inscríbase en la lista de espera de acceso anticipado",
                                TR: "Erken erişim bekleme listesine katılın",
                                CZ: "Připojte se k čekací listině pro včasný přístup",
                                EL: "Εγγραφείτε στο πρόγραμμα early-access και αποκτήστε πρώτοι πρόσβαση",
                                FR: "Inscrivez-vous dès maintenant sur la liste d’attente pour un accès anticipé.",
                                NL: "Meld je aan voor de wachtlijst voor vroege toegang",
                                EN: "Join the early-access waitlist",
                            })}
                            className="bg-black text-white"
                        />
                    </div>
                </div>
                <div className="relative md:order-2 order-1 pointer-events-none">
                    <div className="absolute hidden md:block left-[-50px] top-0 w-full rotate-[70deg]">
                        <div className="-mx-[1000px] h-[60px] bg-reonic-yellow" />
                        <div className="-mx-[1000px] h-[60px] bg-reonic-green" />
                        <div className="-mx-[1000px] h-[60px] bg-reonic-red" />
                        <div className="-mx-[1000px] h-[60px] bg-reonic-blue" />
                    </div>
                    <div className="max-w-full w-full md:w-[800px] z-1 relative">
                        <Img
                            className="w-[138%] -ml-[38%]"
                            noPreload
                            size="xl"
                            alt=""
                            src="/mirror/landing_page_expansion/Int - Hero.png"
                        />
                    </div>
                </div>
                <div className="absolute bg-gradient-to-t from-white to-transparent bottom-0 left-0 w-full flex flex-col gap-y-3 items-center md:h-60" />
                <div className="absolute bg-gradient-to-t z-40 order-3 from-white to-transparent bottom-0 left-0 w-full flex flex-col gap-y-3 items-center md:pt-14 pb-8">
                    <div className="flex gap-x-8 flex-wrap justify-center w-full items-center">
                        <div className="flex items-center justify-center h-[40px]">
                            <Img
                                noPreload
                                size="xxs"
                                alt=""
                                className="h-[50%]"
                                src="/pages/de-de/industry/dealerships/references/lichtblick.svg"
                            />
                        </div>

                        <div className="flex items-center justify-center h-[40px]">
                            <Img
                                noPreload
                                size="xxs"
                                alt=""
                                className="h-[50%] -mr-3"
                                src="/pages/de-de/industry/solar/references/svea_logo.png"
                            />
                        </div>

                        <div className="flex items-center justify-center h-[40px]">
                            <Img
                                noPreload
                                size="xxs"
                                alt=""
                                className="h-[50%]"
                                src="/pages/de-de/industry/solar/references/New_logo.svg.png"
                            />
                        </div>

                        <div className=" items-center justify-center h-[40px] flex">
                            <Img
                                noPreload
                                size="xxs"
                                alt=""
                                className="h-[60%] -mr-4"
                                src="/pages/de-de/industry/solar/references/pvgreen-logo.svg"
                            />
                        </div>

                        <div className=" items-center justify-center h-[40px] flex">
                            <Img
                                noPreload
                                size="xxs"
                                alt=""
                                className="h-[60%] -mr-2 "
                                src="/pages/de-de/industry/dealerships/references/invanova.png"
                            />
                        </div>

                        <div className=" items-center justify-center h-[40px] flex">
                            <Img
                                noPreload
                                size="xxs"
                                alt=""
                                className="h-[140%] -mt-1 "
                                src="/pages/de-de/industry/solar/references/calosol_logo.webp"
                            />
                        </div>

                        <div className="mb-1 text-sm text-black font-medium hidden sm:block">
                            {languageSwitch(lang, {
                                // and 600+ more installers trust us
                                ET: "ja veel 600+ paigaldajat usaldavad meid",
                                LT: "ir dar daugiau nei 600 montuotojų mumis pasitiki",
                                LV: "un vēl vairāk nekā 600 uzstādītāju mums uzticas",
                                ES: "y más de 600 instaladores confían en nosotros",
                                TR: "ve 600'den fazla tesisatçı bize güveniyor",
                                CZ: "a dalších více než 600 montážních firem nám důvěřuje",
                                EL: "και 600+ ακόμα εγκαταστάτες μας εμπιστεύονται",
                                FR: "et plus de 600 installateurs nous font confiance",
                                NL: "en meer dan 600 installateurs vertrouwen ons",
                                EN: "and 600+ more installers trust us",
                            })}
                        </div>
                    </div>
                    {/* <div className="flex flex-col md:flex-row gap-5 justify-start items-center">
                        <div className="flex gap-0.5 whitespace-nowrap items-center">
                            <FontAwesomeIcon icon={faStar} className="text-amber-500" />
                            <FontAwesomeIcon icon={faStar} className="text-amber-500" />
                            <FontAwesomeIcon icon={faStar} className="text-amber-500" />
                            <FontAwesomeIcon icon={faStar} className="text-amber-500" />
                            <FontAwesomeIcon icon={faStar} className="text-amber-500" />
                            <Link
                                className="ml-2 whitespace-nowrap text-gray-700 hover:text-gray-500 text-sm font-medium"
                                to="https://g.co/kgs/wNxCGiS"
                                target="_blank"
                            >
                                5/5 &middot; Google
                            </Link>
                        </div>
                        <div className="flex gap-1 whitespace-nowrap items-center">
                            <div className="bg-black rounded-sm w-5 h-5 flex items-center justify-center">
                                <FontAwesomeIcon icon={faStar} className="text-green-500 text-xs" />
                            </div>
                            <div className="bg-black rounded-sm w-5 h-5 flex items-center justify-center">
                                <FontAwesomeIcon icon={faStar} className="text-green-500 text-xs" />
                            </div>
                            <div className="bg-black rounded-sm w-5 h-5 flex items-center justify-center">
                                <FontAwesomeIcon icon={faStar} className="text-green-500 text-xs" />
                            </div>
                            <div className="bg-black rounded-sm w-5 h-5 flex items-center justify-center">
                                <FontAwesomeIcon icon={faStar} className="text-green-500 text-xs" />
                            </div>
                            <div className="bg-black rounded-sm w-5 h-5 flex items-center justify-center">
                                <FontAwesomeIcon icon={faStar} className="text-green-500 text-xs" />
                            </div>
                            <Link
                                className="ml-2 whitespace-nowrap text-gray-700 hover:text-gray-500 text-sm font-medium"
                                to="#"
                                // className="ml-2 whitespace-nowrap text-gray-700 hover:text-gray-500 text-sm font-medium"
                                // to="https://www.trustpilot.com/review/reonic.de?languages=all&sort=recency"
                                target="_blank"
                            >
                                Excellent &middot; Trustpilot
                            </Link>
                        </div>
                    </div> */}
                </div>
            </Section>
            {/* Why Reonic */}
            <Section className="md:pt-44 pt-20 pb-28" gray id="why-reonic">
                <div className="text-sm font-medium mb-2 2xl:mb-3 text-yellow-500">
                    {languageSwitch(lang, {
                        // Why Reonic?
                        ET: "Miks Reonic?",
                        LT: "Kodėl Reonikas?",
                        LV: "Kāpēc Reonic?",
                        ES: "¿Por qué elegir Reonic?",
                        TR: "Neden Reonic?",
                        CZ: "Proč Reonic?",
                        EL: "Γιατί να επιλέξετε το Reonic;",
                        FR: "Pourquoi Reonic ?",
                        NL: "Waarom Reonic?",
                        EN: "Why Reonic?",
                    })}
                </div>
                <h2 className="text-black font-bold text-d-5xl mb-14">
                    {languageSwitch(lang, {
                        // A software solution
                        ET: "Tarkvaralahendus",
                        LT: "Programinės įrangos sprendimas",
                        LV: "Programmatūras risinājums",
                        ES: "Una solución informática",
                        TR: "Bir yazılım çözümü",
                        CZ: "Softwarové řešení",
                        EL: "Μια λύση λογισμικού",
                        FR: "Une solution logicielle",
                        NL: "Een softwareoplossing",
                        EN: "A software solution",
                    })}
                    <br />
                    {languageSwitch(lang, {
                        // made just for you.
                        ET: "tehtud just sinu jaoks.",
                        LT: "sukurtas tik tau.",
                        LV: "radīts tieši tev.",
                        ES: "hecha a su medida.",
                        TR: "sadece senin için yapılmış.",
                        CZ: "vyrobený přímo pro vás.",
                        EL: "σχεδιασμένη μόνο για εσάς.",
                        FR: "conçue spécialement pour vous.",
                        NL: "speciaal voor jou gemaakt.",
                        EN: "made just for you.",
                    })}
                </h2>
                <div className="grid md:grid-cols-3 sm:grid-cols-2 gap-10">
                    <div>
                        <div className="font-bold sm:min-h-12 text-d-xl">
                            {languageSwitch(lang, {
                                // Photovoltaics, storage, wallbox and heat pump. All in one software.
                                ET: "Fotogalvaanika, salvestamine, wallbox ja soojuspump. Kõik ühes tarkvaras.",
                                LT: "Fotovoltinė energija, saugykla, sieninė dėžutė ir šilumos siurblys. Viskas vienoje programinėje įrangoje.",
                                LV: "Fotogalvaniskie elementi, krātuve, sienas kaste un siltumsūknis. Viss vienā programmatūrā.",
                                ES: "Fotovoltaica, acumuladores, wallbox, aire acondicionado y bombas de calor. Todo en un solo software.",
                                TR: "Fotovoltaikler, depolar, duvar kutuları, klima ve ısı pompaları. Hepsi tek bir yazılımda.",
                                CZ: "Fotovoltaika, úložiště, wallboxy, klimatizace a tepelná čerpadla. Vše v jednom softwaru.",
                                EL: "Φωτοβολταϊκά, στοιχεία μπαταριών, wallbox φόρτισης και αντλίες θερμότητας. Όλα σε ένα λογισμικό.",
                                FR: "Photovoltaïque, stockage, bornes de recharge, climatisation et pompes à chaleur : un seul logiciel pour tout gérer.",
                                NL: "Fotovoltaïsche energie, opslag, laadpalen en warmtepompen. Alles in één software.",
                                EN: "Photovoltaics, storage, wallbox and heat pump. All in one software.",
                            })}
                        </div>
                        <div className="text-sm text-gray-500 space-y-2 mt-3">
                            <Bullet
                                icon={faChevronCircleRight}
                                twIconClassName="text-yellow-500"
                                className="text-zinc-500 text-lg"
                                title={languageSwitch(lang, {
                                    // Design, plan and install.
                                    ET: "Projekteeri, planeeri ja paigalda.",
                                    LT: "Suprojektuokite, suplanuokite ir sumontuokite.",
                                    LV: "Projektēšana, plānošana un uzstādīšana.",
                                    ES: "Diseñar, planificar e instalar.",
                                    TR: "Tasarım, planlama ve kurulum.",
                                    CZ: "Návrh, plánování a instalace.",
                                    EL: "Μελέτη, προγραμματισμός και εγκατάσταση.",
                                    FR: "Conception, calepinage et installation simplifiées et automatisés.",
                                    NL: "Ontwerp, plan en installeer.",
                                    EN: "Design, plan and install.",
                                })}
                            />
                            <Bullet
                                icon={faChevronCircleRight}
                                twIconClassName="text-yellow-500"
                                className="text-zinc-500 text-lg"
                                title={languageSwitch(lang, {
                                    // All renewable components.
                                    ET: "Kõik taastuvad komponendid.",
                                    LT: "Visi atsinaujinantys komponentai.",
                                    LV: "Visas atjaunojamās sastāvdaļas.",
                                    ES: "Todos los componentes renovables.",
                                    TR: "Tüm yenilenebilir bileşenler.",
                                    CZ: "Všechny obnovitelné složky.",
                                    EL: "Όλα τα συστατικά ανανεώσιμων πηγών ενέργειας.",
                                    FR: "Compatible avec toutes les technologies renouvelables.",
                                    NL: "Alle duurzame componenten.",
                                    EN: "All renewable components.",
                                })}
                            />
                            <Bullet
                                icon={faChevronCircleRight}
                                twIconClassName="text-yellow-500"
                                className="text-zinc-500 text-lg"
                                title={languageSwitch(lang, {
                                    // Manufacturer-independent and flexible.
                                    ET: "Tootjast sõltumatu ja paindlik.",
                                    LT: "Nepriklausomas nuo gamintojo ir lankstus.",
                                    LV: "Neatkarīgi no ražotāja un elastīgi.",
                                    ES: "Independiente del fabricante y flexible.",
                                    TR: "Üreticiden bağımsız ve esnek.",
                                    CZ: "Nezávislost na výrobci a flexibilita.",
                                    EL: "Σας παρέχει ανεξαρτησία από συγκεκριμένους κατασκευαστές και ευελιξία.",
                                    FR: "Indépendant des fabricants, flexible et évolutif.",
                                    NL: "Onafhankelijk van de fabrikant en flexibel.",
                                    EN: "Manufacturer-independent and flexible.",
                                })}
                            />
                        </div>
                    </div>
                    <div>
                        <div className="font-bold sm:min-h-12 text-d-xl">
                            {languageSwitch(lang, {
                                // All steps from initial customer contact to installation in one tool.
                                ET: "Kõik sammud alates esimesest kliendikontaktist kuni paigaldamiseni ühes tööriistas.",
                                LT: "Visi žingsniai nuo pirmojo kontakto su klientu iki įdiegimo viename įrankyje.",
                                LV: "Visi soļi no sākotnējā kontakta ar klientu līdz uzstādīšanai vienā rīkā.",
                                ES: "Todos los pasos, desde el contacto inicial con el cliente hasta la instalación, en una sola herramienta.",
                                TR: "İlk müşteri temasından kuruluma kadar tüm adımlar tek bir araçta.",
                                CZ: "Všechny kroky od prvního kontaktu se zákazníkem až po instalaci v jednom nástroji.",
                                EL: "Όλα τα βήματα από την αρχική επαφή με τον πελάτη έως την εγκατάσταση σε ένα εργαλείο.",
                                FR: "Toutes les étapes, du premier contact client à l’installation, centralisées dans un seul outil.",
                                NL: "Alle stappen van het eerste klantcontact tot de installatie in één tool.",
                                EN: "All steps from initial customer contact to installation in one tool.",
                            })}
                        </div>
                        <div className="text-sm text-gray-500 space-y-2 mt-3">
                            <Bullet
                                icon={faChevronCircleRight}
                                className="text-zinc-500 text-lg"
                                twIconClassName="text-yellow-500"
                                title={languageSwitch(lang, {
                                    // Your operating system for the sale and installation of renewable technologies
                                    ET: "Teie operatsioonisüsteem taastuvenergia tehnoloogiate müügiks ja paigaldamiseks",
                                    LT: "Jūsų atsinaujinančiųjų išteklių technologijų pardavimo ir diegimo operacinė sistema",
                                    LV: "Jūsu operētājsistēma atjaunojamo energoresursu tehnoloģiju pārdošanai un uzstādīšanai",
                                    ES: "Su sistema operativo para la venta e instalación de tecnologías renovables",
                                    TR: "Yenilenebilir teknolojilerin satışı ve kurulumu için işletim sisteminiz",
                                    CZ: "Váš operační systém pro prodej a instalaci technologií obnovitelných zdrojů energie",
                                    EL: "Το λειτουργικό σας σύστημα για την πώληση και εγκατάσταση τεχνολογιών ανανεώσιμων πηγών ενέργειας",
                                    FR: "Votre système d'exploitation pour la vente et l'installation de technologies renouvelables",
                                    NL: "Uw besturingssysteem voor de verkoop en installatie van duurzame technologieën",
                                    EN: "Your operating system for the sale and installation of renewable technologies",
                                })}
                            />
                            <Bullet
                                icon={faChevronCircleRight}
                                twIconClassName="text-yellow-500"
                                className="text-zinc-500 text-lg"
                                title={languageSwitch(lang, {
                                    // Keep track of your customers from a single tool.
                                    ET: "Jälgige oma kliente ühest vahendist.",
                                    LT: "Stebėkite savo klientus naudodami vieną įrankį.",
                                    LV: "Sekojiet saviem klientiem, izmantojot vienu rīku.",
                                    ES: "Realice un seguimiento de sus clientes desde una única herramienta.",
                                    TR: "Müşterilerinizi tek bir araçtan takip edin.",
                                    CZ: "Sledujte své zákazníky z jediného nástroje.",
                                    EL: "Παρακολούθηση των πελατών σας από ένα μόνο εργαλείο.",
                                    FR: "Suivez vos clients à partir d'un seul outil.",
                                    NL: "Houd uw klanten bij vanuit één tool.",
                                    EN: "Keep track of your customers from a single tool.",
                                })}
                            />
                        </div>
                    </div>
                    <div className="hidden">
                        <div className="font-bold sm:min-h-12 text-d-xl">
                            {languageSwitch(lang, {
                                // Flexibility to map your processes in a way that suits your company.
                                ET: "Paindlikkus, et kaardistada oma protsessid nii, et need sobiksid teie ettevõttele.",
                                LT: "Lankstumas, leidžiantis planuoti procesus taip, kaip tinka jūsų įmonei.",
                                LV: "Elastība, lai kartētu procesus jūsu uzņēmumam piemērotā veidā.",
                                ES: "Flexibilidad para asignar sus procesos de forma que se adapten a su empresa.",
                                TR: "Süreçlerinizi şirketinize uygun bir şekilde haritalama esnekliği.",
                                CZ: "Flexibilita při mapování procesů způsobem, který vyhovuje vaší společnosti.",
                                EL: "Ευελιξία στη χαρτογράφηση των διαδικασιών σας με τρόπο που ταιριάζει στην εταιρεία σας.",
                                FR: "Flexibilité pour cartographier vos processus d'une manière qui convient à votre entreprise.",
                                NL: "Flexibiliteit om uw processen in kaart te brengen op een manier die bij uw bedrijf past.",
                                EN: "Flexibility to map your processes in a way that suits your company.",
                            })}
                        </div>
                        <div className="text-sm text-gray-500 mt-2">
                            <Bullet
                                icon={faChevronCircleRight}
                                className="text-zinc-500 text-lg"
                                twIconClassName="text-yellow-500"
                                title={languageSwitch(lang, {
                                    // Optimised for digital collaboration
                                    ET: "Optimeeritud digitaalseks koostööks",
                                    LT: "Optimizuotas skaitmeniniam bendradarbiavimui",
                                    LV: "Optimizēts digitālajai sadarbībai",
                                    ES: "Optimizado para la colaboración digital",
                                    TR: "Dijital işbirliği için optimize edildi",
                                    CZ: "Optimalizováno pro digitální spolupráci",
                                    EL: "Βελτιστοποιημένη ψηφιακή συνεργασία",
                                    FR: "Optimisé pour la collaboration numérique",
                                    NL: "Geoptimaliseerd voor digitale samenwerking",
                                    EN: "Optimised for digital collaboration",
                                })}
                            />
                            <Bullet
                                icon={faChevronCircleRight}
                                className="text-zinc-500 text-lg"
                                twIconClassName="text-yellow-500"
                                title={languageSwitch(lang, {
                                    // Email and calendar interfaces to keep an eye on everything
                                    ET: "E-posti ja kalendri liidesed, et kõigel silma peal hoida",
                                    LT: "El. pašto ir kalendoriaus sąsajos, kad viską stebėtumėte",
                                    LV: "E-pasta un kalendāra saskarnes, lai visu varētu sekot līdzi",
                                    ES: "Interfaces de correo electrónico y calendario para tenerlo todo controlado",
                                    TR: "Her şeyi takip etmek için e-posta ve takvim arayüzleri",
                                    CZ: "Rozhraní e-mailu a kalendáře, abyste měli o všem přehled",
                                    EL: "Διασυνδέσεις ηλεκτρονικού ταχυδρομείου και ημερολογίου για να παρακολουθείτε τα πάντα.",
                                    FR: "Interfaces de messagerie et de calendrier pour garder un œil sur tout",
                                    NL: "E-mail- en agenda-interfaces om alles in de gaten te houden",
                                    EN: "Email and calendar interfaces to keep an eye on everything",
                                })}
                            />
                        </div>
                    </div>

                    <div>
                        <div className="font-bold w-11 rounded-md aspect-square flex items-center justify-center mb-3 bg-zinc-200 hidden">
                            <FontAwesomeIcon icon={faChartLineUp} className="text-md text-reonic-red- text-zinc-800" />
                        </div>
                        <div className="font-bold sm:min-h-12 text-d-xl">
                            {languageSwitch(lang, {
                                // For your private and commercial customer projects. From small to large.
                                ET: "Teie era- ja äriklientide projektide jaoks. Alates väikestest kuni suurteni.",
                                LT: "Jūsų privatiems ir komerciniams klientų projektams. Nuo mažų iki didelių.",
                                LV: "Jūsu privātiem un komerciāliem klientu projektiem. No maziem līdz lieliem.",
                                ES: "Para sus proyectos de clientes privados y comerciales. De pequeños a grandes.",
                                TR: "Özel ve ticari müşteri projeleriniz için. Küçükten büyüğe.",
                                CZ: "Pro projekty soukromých i komerčních zákazníků. Od malých až po velké.",
                                EL: "Για τα έργα ιδιωτών και εμπορικών πελατών σας. Από μικρά έως μεγάλα.",
                                FR: "Pour vos projets privés et commerciaux. Du plus petit au plus grand.",
                                NL: "Voor uw particuliere en zakelijke klantprojecten. Van klein tot groot.",
                                EN: "For your private and commercial customer projects. From small to large.",
                            })}
                        </div>
                        <div className="text-sm text-gray-500 space-y-2 mt-3">
                            <Bullet
                                icon={faChevronCircleRight}
                                className="text-zinc-500 text-lg"
                                twIconClassName="text-yellow-500"
                                title={languageSwitch(lang, {
                                    ET: "Kiire ja tõhus planeerimine eraklientide projektide jaoks.",
                                    LT: "Greitas ir efektyvus privačių klientų projektų planavimas.",
                                    LV: "Ātra un efektīva privāto klientu projektu plānošana.",
                                    ES: "Planificación rápida y eficaz de proyectos de clientes privados.",
                                    TR: "Özel müşteri projeleri için hızlı ve etkili planlama.",
                                    CZ: "Rychlé a efektivní plánování projektů soukromých zákazníků.",
                                    EL: "Γρήγορος και αποτελεσματικός σχεδιασμός για έργα ιδιωτών πελατών.",
                                    FR: "Planification rapide et efficace des projets résidentiels.",
                                    NL: "Snelle en efficiënte planning voor particuliere klantprojecten.",
                                    EN: "Fast and effective planning for private customer projects.",
                                })}
                            />
                            <Bullet
                                icon={faChevronCircleRight}
                                className="text-zinc-500 text-lg"
                                twIconClassName="text-yellow-500"
                                title={languageSwitch(lang, {
                                    ET: "Põhjalikud simulatsioonid ja hinnapakkumised, isegi keerukate energiasüsteemide ja kaubandusprojektide puhul.",
                                    LT: "išsamūs modeliavimai ir pasiūlymai, net ir sudėtingoms energetikos sistemoms bei komerciniams projektams.",
                                    LV: "Visaptveroša simulācija un cenu piedāvājumi pat sarežģītām energosistēmām un komerciāliem projektiem.",
                                    ES: "Simulaciones y presupuestos completos, incluso para sistemas energéticos complejos y proyectos comerciales",
                                    TR: "Karmaşık enerji sistemleri ve ticari projeler için bile kapsamlı simülasyonlar ve fiyat teklifleri",
                                    CZ: "Komplexní simulace a cenové nabídky, a to i pro složité energetické systémy a komerční projekty.",
                                    EL: "Ολοκληρωμένες προσομοιώσεις και προσφορές, ακόμη και για πολύπλοκα ενεργειακά συστήματα και εμπορικά έργα",
                                    FR: "Simulations précises et devis détaillés, même pour les systèmes les plus complexes.",
                                    NL: "Uitgebreide simulaties en offertes, zelfs voor complexe energiesystemen en commerciële projecten",
                                    EN: "Comprehensive simulations and quotations, even for complex energy systems and commercial projects",
                                })}
                            />
                        </div>
                    </div>
                </div>
                <div className="mt-14">
                    <LinkAsButton to="#waitlist" className="bg-black text-white">
                        {languageSwitch(lang, {
                            // Join the waitlist
                            ET: "Liitu ootenimekirjaga",
                            LT: "Prisijunkite prie laukiančiųjų sąrašo",
                            LV: "Pievienojies gaidīšanas sarakstam",
                            ES: "Inscribirse en la lista de espera",
                            TR: "Bekleme listesine katılın",
                            CZ: "Připojte se k čekací listině",
                            EL: "Εγγραφείτε στη λίστα αναμονής",
                            FR: "S'inscrire sur la liste d'attente",
                            NL: "Schrijf je in voor de wachtlijst",
                            EN: "Join the waitlist",
                        })}
                    </LinkAsButton>
                </div>
                <div className="flex gap-3 mt-20">
                    <div>
                        <div className="text-gray-500 text-xs mb-4">
                            {languageSwitch(lang, {
                                // Known from
                                ET: "Tuntud",
                                LT: "Žinomas iš",
                                LV: "Zināms no",
                                ES: "Conocido por",
                                TR: "Bilinen",
                                CZ: "Známé z",
                                EL: "Γνωστοί από",
                                FR: "Connu de",
                                NL: "Bekend van",
                                EN: "Known from",
                            })}
                        </div>
                        <div className="flex gap-5 items-center h-[40px] flex-wrap">
                            <TrackedExternalLink
                                target="_blank"
                                href="https://techcrunch.com/2024/09/04/reonic-raises-e13-million-to-help-small-installers-of-green-tech-like-heat-pumps-and-solar-panels/"
                            >
                                <Img className="h-[30px]" size="xxs" alt="" src="/pages/de-de/press_tc.svg" />
                            </TrackedExternalLink>
                            <TrackedExternalLink
                                target="_blank"
                                href="https://www.startup-insider.com/podcast/volocopter-chef-geht-verwirrung-um-finanzierungsrunde-bei"
                            >
                                <Img className="h-[30px]" size="xxs" alt="" src="/pages/de-de/press_si.png" />
                            </TrackedExternalLink>
                            <TrackedExternalLink
                                target="_blank"
                                href="https://www.deutsche-startups.de/2024/09/05/dealmonitor-reonic-hello-inside/"
                            >
                                <Img className="h-[30px]" size="xxs" alt="" src="/pages/de-de/press_ds.png" />
                            </TrackedExternalLink>
                            <TrackedExternalLink
                                target="_blank"
                                href="https://www.energate-messenger.de/news/247197/it-start-up-reonic-sammelt-13-mio.-euro-bei-investoren"
                            >
                                <Img className="h-[30px]" size="xxs" alt="" src="/pages/de-de/press_energate.png" />
                            </TrackedExternalLink>
                            <TrackedExternalLink
                                target="_blank"
                                href="https://www.pv-magazine.de/unternehmensmeldungen/digitale-loesung-fuer-ausbau-erneuerbarer-energien-in-ganz-europa/"
                            >
                                <Img className="h-[50px]" size="xxs" alt="" src="/pages/de-de/press_pvmagazine.png" />
                            </TrackedExternalLink>
                        </div>
                    </div>
                </div>
            </Section>
            {/* Step 1 */}
            <Section className="py-44" relative={false} id="functionality">
                <Img
                    className="w-[calc(100%+2*3.5%)] -mx-[3.5%]"
                    size="xxl"
                    alt=""
                    src="/mirror/landing_page_expansion/Int - Leadgen.png"
                />
                <>
                    <div className="text-sm font-medium mb-2 2xl:mb-3 text-reonic-green">
                        {languageSwitch(lang, {
                            // Step 1
                            ET: "1. samm",
                            LT: "1 žingsnis",
                            LV: "1. solis",
                            ES: "Primer paso",
                            TR: "Adım 1",
                            CZ: "Krok 1",
                            EL: "Βήμα 1",
                            FR: "Étape 1",
                            NL: "Stap 1",
                            EN: "Step 1",
                        })}
                    </div>
                    <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black">
                        {languageSwitch(lang, {
                            ET: "Põnevate veebilehe külastajate loomine, juhtide loomine ja nende kvalifitseerimine.",
                            LT: "Sudominkite svetainės lankytojus, generuokite potencialius klientus ir juos kvalifikuokite.",
                            LV: "Aizraut tīmekļa vietnes apmeklētājus, radīt potenciālos klientus un atlasīt tos.",
                            ES: "Llame la atención de los visitantes al sitio web, genere clientes potenciales y cualifíquelos.",
                            TR: "Web sitesi ziyaretçilerini heyecanlandırın, potansiyel müşteriler oluşturun ve onları nitelendirin.",
                            CZ: "Zaujměte návštěvníky webových stránek, generujte potenciální zákazníky a kvalifikujte je.",
                            EL: "Ενθουσιάστε τους επισκέπτες του ιστοτόπου σας, δημιουργήστε συνδέσμους και προσδιορίστε τους.",
                            FR: "Attirez les visiteurs de votre site, transformez-les en prospects et qualifiez-les.",
                            NL: "Betrek websitebezoekers, genereer leads en kwalificeer ze.",
                            EN: "Engage website visitors, generate leads, and qualify them.",
                        })}
                    </div>
                    <div className="mt-2 mb-1.5 text-lg font-medium text-black">
                        {languageSwitch(lang, {
                            // Fewer questions for you, more time for the important things. Excite and convince your visitors to become leads with an immediate first simulation on your home page.
                            ET: "Vähem küsimusi teile, rohkem aega olulistele asjadele. Erguta ja veena oma külastajaid, et nad muutuksid kohe esimese simulatsiooniga sinu koduleheküljel.",
                            LT: "Mažiau klausimų jums, daugiau laiko svarbiems dalykams. Sudominkite ir įtikinkite lankytojus tapti potencialiais klientais, iš karto atlikdami pirmąją simuliaciją pagrindiniame puslapyje.",
                            LV: "Mazāk jautājumu jums, vairāk laika svarīgām lietām. Aizraut un pārliecināt apmeklētājus kļūt par līderiem ar tūlītēju pirmo simulāciju savā sākumlapā.",
                            ES: "Menos preguntas para usted, más tiempo para lo importante. Emocione y convenza a sus visitantes para que se conviertan en clientes potenciales con una primera simulación inmediata en su página de inicio.",
                            TR: "Sizin için daha az soru, önemli şeyler için daha fazla zaman. Ana sayfanızda anında bir ilk simülasyon ile ziyaretçilerinizi heyecanlandırın ve potansiyel müşteri olmaya ikna edin.",
                            CZ: "Méně otázek pro vás, více času na důležité věci. Nadchněte a přesvědčte návštěvníky, aby se stali potenciálními zákazníky, pomocí okamžité první simulace na domovské stránce.",
                            EL: "Λιγότερες ερωτήσεις για εσάς, περισσότερος χρόνος για τα σημαντικά πράγματα. Ενθουσιάστε και πείστε τους επισκέπτες σας να γίνουν πελάτες με μια άμεση πρώτη προσομοίωση στην αρχική σας σελίδα.",
                            FR: "Moins de questions à gérer, plus de temps pour ce qui compte vraiment. Captez l’attention de vos visiteurs et incitez-les à devenir des prospects grâce à une simulation instantanée directement intégrée à votre page d’accueil.",
                            NL: "Minder vragen voor jou, meer tijd voor de belangrijke dingen. Prikkel en overtuig je bezoekers om leads te worden met een onmiddellijke eerste simulatie op je startpagina.",
                            EN: "Fewer questions for you, more time for the important things. Encourage your visitors to become leads with an immediate simulation on your home page.",
                        })}
                    </div>
                    <div className="flex flex-col gap-2 text-zinc-500 mt-3 mb-5">
                        <Bullet
                            icon={faChevronCircleRight}
                            className="text-sm"
                            title={languageSwitch(lang, {
                                // Digital: Present complex subjects interactively for the end customer.
                                ET: "Digitaalne: esitada keerulisi teemasid interaktiivselt lõppkliendile.",
                                LT: "Skaitmeninis: interaktyviai pateikite sudėtingus dalykus galutiniam klientui.",
                                LV: "Digitāls: Sarežģītus tematus interaktīvi pasniedziet gala klientam.",
                                ES: "Digital: presente temas complejos de forma interactiva para el cliente final.",
                                TR: "Dijital: Son müşteri için karmaşık konuları etkileşimli olarak sunun.",
                                CZ: "Digitální: Prezentujte složitá témata interaktivně pro koncového zákazníka.",
                                EL: "Ψηφιακά: Παρουσιάστε πολύπλοκα θέματα διαδραστικά για τον τελικό πελάτη.",
                                FR: "Numérique : Présenter des sujets complexes de manière interactive pour le client final.",
                                NL: "Digitaal: Presenteer complexe onderwerpen interactief voor de eindklant.",
                                EN: "Digital: Present complex subjects interactively for the end customer.",
                            })}
                        />
                        <Bullet
                            icon={faChevronCircleRight}
                            className="text-sm"
                            title={languageSwitch(lang, {
                                // Holistic: Cover all relevant renewable technologies.
                                ET: "Holistiline: Katab kõik asjakohased taastuvenergia tehnoloogiad.",
                                LT: "Holistinis: Apima visas susijusias atsinaujinančių išteklių technologijas.",
                                LV: "Holistisks: aptver visas attiecīgās atjaunojamo energoresursu tehnoloģijas.",
                                ES: "Holístico: abarque todas las tecnologías renovables pertinentes.",
                                TR: "Bütünsel: İlgili tüm yenilenebilir teknolojileri kapsar.",
                                CZ: "Holistické: Zahrnuje všechny relevantní technologie obnovitelných zdrojů.",
                                EL: "Ολιστικά: Καλύπτει όλες τις σχετικές τεχνολογίες ανανεώσιμων πηγών ενέργειας.",
                                FR: "Holistique : Couvrir toutes les technologies renouvelables pertinentes.",
                                NL: "Holistisch: Omvat alle relevante duurzame technologieën.",
                                EN: "Holistic: Cover all relevant renewable technologies.",
                            })}
                        />
                        <Bullet
                            icon={faChevronCircleRight}
                            className="text-sm"
                            title={languageSwitch(lang, {
                                // Efficient: Enable self-service information and advice for the customer.
                                ET: "Tõhus: Võimaldage kliendile iseteeninduslik teave ja nõustamine.",
                                LT: "Efektyvus: Efektyvus: suteikite galimybę klientui gauti savitarnos informaciją ir patarimus.",
                                LV: "Efektīvi: Nodrošināt pašapkalpošanās informācijas un padomu sniegšanu klientam.",
                                ES: "Eficiente: permita el autoservicio de información y asesoramiento al cliente.",
                                TR: "Etkili: Müşteri için self servis bilgi ve tavsiyeye olanak sağlayın.",
                                CZ: "Efektivní: Umožněte zákazníkovi samoobslužné poskytování informací a poradenství.",
                                EL: "Αποτελεσματικά: Ενεργοποίηση της αυτοεξυπηρέτησης των πελατών στη φάση της συλλογής πληροφοριών και συμβουλών.",
                                FR: "Efficace : Permettre au client d'obtenir des informations et des conseils en libre-service.",
                                NL: "Efficiënt: Bied zelfbedieningsinformatie en advies aan de klant.",
                                EN: "Efficient: Enable self-service information and advice for the customer.",
                            })}
                        />
                    </div>
                    <div className="flex mt-3">
                        <LinkAsButton
                            to="#waitlist"
                            className="font-medium w-fit flex items-center text-sm py-1.5 bg-reonic-green text-white"
                        >
                            {languageSwitch(lang, {
                                // Join the waitlist
                                ET: "Liitu ootenimekirjaga",
                                LT: "Prisijunkite prie laukiančiųjų sąrašo",
                                LV: "Pievienojies gaidīšanas sarakstam",
                                ES: "Inscribirse en la lista de espera",
                                TR: "Bekleme listesine katılın",
                                CZ: "Připojte se k čekací listině",
                                EL: "Εγγραφείτε στη λίστα αναμονής",
                                FR: "S'inscrire sur la liste d'attente",
                                NL: "Schrijf je in voor de wachtlijst",
                                EN: "Join the waitlist",
                            })}
                        </LinkAsButton>
                    </div>
                </>
            </Section>
            {/* Step 2 */}
            <Section className="py-44" relative={false}>
                <div className="absolute left-0 top-0 bg-gradient-to-b from-gray-100 to-white w-full h-full -z-10" />
                <Img
                    size="xl"
                    className="w-[calc(100%+2*8%)] -mx-[8%]"
                    src="/mirror/landing_page_expansion/Int - Planning.png"
                    alt=""
                />
                <div className="text-sm font-medium mb-3 text-reonic-yellow">
                    {languageSwitch(lang, {
                        // Step 2
                        ET: "2. samm",
                        LT: "2 žingsnis",
                        LV: "2. solis",
                        ES: "Paso 2",
                        TR: "Adım 2",
                        CZ: "Krok 2",
                        EL: "Βήμα 2",
                        FR: "Étape 2",
                        NL: "Stap 2",
                        EN: "Step 2",
                    })}
                </div>
                <div className="text-d-5xl font-bold">
                    {languageSwitch(lang, {
                        // Plan complete energy systems for your customer, all in one software and from everywhere.
                        ET: "Planeeri oma kliendi jaoks terviklikud energiasüsteemid, kõik ühes tarkvaras ja kõikjalt.",
                        LT: "Suplanuokite visas energijos sistemas savo klientui, naudodami vieną programinę įrangą ir iš bet kurios vietos.",
                        LV: "Plānojiet pilnīgas energosistēmas savam klientam, izmantojot vienu programmatūru un no jebkuras vietas.",
                        ES: "Planifique sistemas energéticos completos para su cliente, todo en un solo software y desde cualquier lugar.",
                        TR: "Müşteriniz için eksiksiz enerji sistemlerini tek bir yazılımla ve her yerden planlayın.",
                        CZ: "Plánujte kompletní energetické systémy pro své zákazníky, vše v jednom softwaru a odkudkoli.",
                        EL: "Σχεδιάστε πλήρη ενεργειακά συστήματα για τον πελάτη σας, όλα σε ένα λογισμικό και από παντού.",
                        FR: "Planifiez des systèmes énergétiques complets pour vos clients, avec un seul logiciel, où que vous soyez.",
                        NL: "Plan complete energiesystemen voor je klant, alles in één software en overal vandaan.",
                        EN: "Plan complete energy systems for your customer, all in one software, from everywhere.",
                    })}
                </div>
                <div className="mt-2 mb-1.5 text-xl text-black">
                    {languageSwitch(lang, {
                        // With Reonic, you can plan photovoltaic panels, battery storage, wallboxes and heat pumps.
                        ET: "Reoniciga saate planeerida fotogalvaanilisi paneele, akusalvestusi, seinakarbid ja soojuspumbad.",
                        LT: "Naudodami Reonic galite planuoti fotovoltines plokštes, akumuliatorių saugyklas, sienines dėžutes ir šilumos siurblius.",
                        LV: "Izmantojot Reonic, varat plānot fotoelementu paneļus, akumulatoru krātuves, sienas kastes un siltumsūkņus.",
                        ES: "Con Reonic puede planificar paneles fotovoltaicos, baterías, wallboxes y bombas de calor.",
                        TR: "Reonic ile fotovoltaik panelleri, batarya depolamayı, duvar kutularını ve ısı pompalarını planlayabilirsiniz.",
                        CZ: "S aplikací Reonic můžete plánovat fotovoltaické panely, bateriová úložiště, wallboxy a tepelná čerpadla.",
                        EL: "Με το Reonic, μπορείτε να σχεδιάσετε φωτοβολταϊκά πάνελ, στοιχεία μπαταριών, wallbox φόρτισης και αντλίες θερμότητας.",
                        FR: "Avec Reonic, vous pouvez planifier des panneaux photovoltaïques, des batteries de stockage, des wallboxes et des pompes à chaleur.",
                        NL: "Met Reonic kun je fotovoltaïsche panelen, batterijopslag, wallboxen en warmtepompen plannen.",
                        EN: "With Reonic, you can plan photovoltaic systems, battery storage, wallboxes and heat pumps.",
                    })}
                </div>
                <div className="space-y-1">
                    <Bullet
                        icon={faChevronCircleRight}
                        className="text-sm "
                        twIconClassName="text-reonic-yellow"
                        title={languageSwitch(lang, {
                            // Install the desired configuration and define the energy consumers and generators.
                            ET: "Paigaldage soovitud konfiguratsioon ning määratlege energiatarbijad ja -generaatorid.",
                            LT: "Įdiekite norimą konfigūraciją ir apibrėžkite energijos vartotojus bei generatorius.",
                            LV: "Uzstādiet vēlamo konfigurāciju un definējiet enerģijas patērētājus un ģeneratorus.",
                            ES: "Instale la configuración deseada y defina los consumidores y generadores de energía.",
                            TR: "İstediğiniz konfigürasyonu kurun ve enerji tüketicileri ile jeneratörleri tanımlayın.",
                            CZ: "Nainstalujte požadovanou konfiguraci a definujte spotřebiče a generátory energie.",
                            EL: "Εγκαταστήστε την επιθυμητή διαμόρφωση και ορίστε τους καταναλωτές και τις γεννήτριες ενέργειας.",
                            FR: "Paramétrez librement la configuration souhaitée et définissez les générateurs ainsi que les consommateurs d’énergie.",
                            NL: "Installeer de gewenste configuratie en definieer de energieverbruikers en opwekkers.",
                            EN: "Install the desired configuration and define the energy consumers and generators.",
                        })}
                    />
                    <Bullet
                        icon={faChevronCircleRight}
                        className="text-sm "
                        twIconClassName="text-reonic-yellow"
                        title={languageSwitch(lang, {
                            // Each component can be individually configured and accesses your product inventory directly.
                            ET: "Iga komponenti saab individuaalselt konfigureerida ja pääseda otse ligi teie tootevalikule.",
                            LT: "Kiekvieną komponentą galima konfigūruoti individualiai ir tiesiogiai pasiekti jūsų produktų inventorių.",
                            LV: "Katru komponentu var konfigurēt individuāli, un tas tieši piekļūst jūsu produktu krājumam.",
                            ES: "Cada componente puede configurarse individualmente y acceder directamente a su inventario de productos.",
                            TR: "Her bileşen ayrı ayrı yapılandırılabilir ve ürün envanterinize doğrudan erişir.",
                            CZ: "Každou komponentu lze individuálně nakonfigurovat a má přímý přístup k vašim zásobám produktů.",
                            EL: "Κάθε στοιχείο μπορεί να διαμορφωθεί ξεχωριστά και έχει άμεση πρόσβαση στην απογραφή των προϊόντων σας.",
                            FR: "Connecté à votre inventaire : chaque composant est entièrement personnalisable et lié à votre bibliothèque de produits.",
                            NL: "Elk onderdeel kan afzonderlijk worden geconfigureerd en heeft rechtstreeks toegang tot je productinventaris.",
                            EN: "Each component can be individually configured and added to your product inventory.",
                        })}
                    />
                </div>
            </Section>
            <Section className="pb-44">
                <Container mode="image-top" xlMode="image-right">
                    <div>
                        <div className="text-d-5xl font-bold">
                            {languageSwitch(lang, {
                                // Perfect planning, made easy.
                                ET: "Täiuslik planeerimine, mis on tehtud lihtsaks.",
                                LT: "Puikus planavimas, kuris tapo paprastas.",
                                LV: "Perfekta plānošana, kas ir vienkārša.",
                                ES: "La planificación perfecta, hecha fácil.",
                                TR: "Mükemmel planlama, kolay hale getirildi.",
                                CZ: "Perfektní plánování, které je snadné.",
                                EL: "Τέλειος σχεδιασμός, εύκολα.",
                                FR: "Une planification parfaite, en toute simplicité.",
                                NL: "Perfecte planning, gemakkelijk gemaakt.",
                                EN: "Perfect planning, made easy.",
                            })}
                        </div>
                        <div className="text-xl mb-5 mt-1">
                            {languageSwitch(lang, {
                                // Digital planning and processes, from A to Z.
                                ET: "Digitaalne planeerimine ja protsessid A-st Z-ni.",
                                LT: "Skaitmeninis planavimas ir procesai nuo A iki Z.",
                                LV: "Digitālā plānošana un procesi no A līdz Z.",
                                ES: "Planificación y procesos digitales, de la A a la Z.",
                                TR: "A'dan Z'ye dijital planlama ve süreçler.",
                                CZ: "Digitální plánování a procesy od A do Z.",
                                EL: "Ψηφιακός σχεδιασμός και διαδικασίες, από το Α έως το Ω.",
                                FR: "Planification et processus numériques, de A à Z.",
                                NL: "Digitale planning en processen, van A tot Z.",
                                EN: "Digital planning and processes, from A to Z.",
                            })}
                        </div>
                        <div className="space-y-1">
                            <Bullet
                                icon={faChevronCircleRight}
                                className="text-sm"
                                twIconClassName="text-reonic-yellow"
                                title={languageSwitch(lang, {
                                    // Fast: Holistic energy planning in under 15 minutes.
                                    ET: "Kiire: terviklik energiaplaneerimine alla 15 minutiga.",
                                    LT: "Greitai: holistinis energijos planavimas per 15 minučių.",
                                    LV: "Ātri: visaptveroša enerģijas plānošana mazāk nekā 15 minūtēs.",
                                    ES: "Rápido: planificación energética integral en menos de 15 minutos.",
                                    TR: "Hızlı: 15 dakikadan kısa sürede bütünsel enerji planlaması.",
                                    CZ: "Rychle: Celostní energetické plánování do 15 minut.",
                                    EL: "Γρήγορα: Ολιστικός ενεργειακός σχεδιασμός σε λιγότερο από 15 λεπτά.",
                                    FR: "Rapide : planification énergétique globale en moins de 15 minutes.",
                                    NL: "Snel: Holistische energieplanning in minder dan 15 minuten.",
                                    EN: "Fast: Holistic energy planning in under 15 minutes.",
                                })}
                            />
                            <Bullet
                                icon={faChevronCircleRight}
                                className="text-sm"
                                twIconClassName="text-reonic-yellow"
                                title={languageSwitch(lang, {
                                    // Practical: Creation of parts lists for price monitoring.
                                    ET: "Praktiline: Hinnaseireks vajalike osade nimekirjade koostamine.",
                                    LT: "Praktiška: Kainų stebėsenai skirtų dalių sąrašų sudarymas.",
                                    LV: "Praktiski: Daļu sarakstu izveide cenu uzraudzībai.",
                                    ES: "Práctico: creación de listas de piezas para el control de precios.",
                                    TR: "Pratik: Fiyat takibi için parça listelerinin oluşturulması.",
                                    CZ: "Praktické: Vytváření seznamů dílů pro sledování cen.",
                                    EL: "Πρακτικά: Δημιουργία καταλόγων ανταλλακτικών για την παρακολούθηση των τιμών.",
                                    FR: "Pratique : Création de listes de pièces pour le suivi des prix.",
                                    NL: "Praktisch: Aanmaken van stuklijsten voor prijsbewaking.",
                                    EN: "Practical: Creation of parts lists for price monitoring.",
                                })}
                            />
                            <Bullet
                                icon={faChevronCircleRight}
                                className="text-sm"
                                twIconClassName="text-reonic-yellow"
                                title={languageSwitch(lang, {
                                    // Up to date: collaborative online planning, directly on your tablet.
                                    ET: "Up to date: ühine veebipõhine planeerimine otse teie tahvelarvutis.",
                                    LT: "Naujausia informacija: bendras planavimas internetu tiesiai planšetiniame kompiuteryje.",
                                    LV: "Aktuāli: kopīga plānošana tiešsaistē tieši jūsu planšetdatorā.",
                                    ES: "Al día: planificación colaborativa online, directamente en su tablet.",
                                    TR: "Güncel: doğrudan tabletinizde işbirliğine dayalı çevrimiçi planlama.",
                                    CZ: "Aktuální: společné online plánování přímo na vašem tabletu.",
                                    EL: "Ενημερωμένα: Συνεργατικός online σχεδιασμός, απευθείας στο τάμπλετ σας.",
                                    FR: "À jour : planification collaborative en ligne, directement sur votre tablette.",
                                    NL: "Up to date: samen online plannen, direct op je tablet.",
                                    EN: "Up to date: Collaborative online planning, directly on your tablet, phone or laptop",
                                })}
                            />
                        </div>
                    </div>
                    <Img
                        size="xl"
                        alt="" //
                        className="w-full xl:w-[150%]"
                        src="/mirror/landing_page_expansion/Int - Plan.png"
                    />
                </Container>
            </Section>
            <Section className="pb-36">
                <Container mode="image-top" xlMode="image-left">
                    <div>
                        <div className="text-d-5xl font-bold">
                            {languageSwitch(lang, {
                                // Accurate simulation.
                                ET: "Täpne simulatsioon.",
                                LT: "Tikslus modeliavimas.",
                                LV: "Precīza simulācija.",
                                ES: "Simulación precisa.",
                                TR: "Doğru simülasyon.",
                                CZ: "Přesná simulace.",
                                EL: "Ακριβής προσομοίωση.",
                                FR: "Simulation précise.",
                                NL: "Nauwkeurige simulatie.",
                                EN: "Accurate simulation.",
                            })}
                        </div>
                        <div className="text-xl mb-5 mt-1">
                            {languageSwitch(lang, {
                                // Complex simulation of energy flows. Meaningful results. Understandable presentation.
                                ET: "Energiavoogude kompleksne simulatsioon. Tähendusrikkad tulemused. Arusaadav esitus.",
                                LT: "Sudėtingas energijos srautų modeliavimas. Reikšmingi rezultatai. Suprantamas pateikimas.",
                                LV: "Enerģijas plūsmu kompleksa simulācija. Nozīmīgi rezultāti. Saprotama prezentācija.",
                                ES: "Simulación compleja de flujos de energía. Resultados significativos. Presentación comprensible.",
                                TR: "Enerji akışlarının karmaşık simülasyonu. Anlamlı sonuçlar. Anlaşılabilir sunum.",
                                CZ: "Komplexní simulace energetických toků. Smysluplné výsledky. Srozumitelná prezentace.",
                                EL: "Πολύπλοκη προσομοίωση ενεργειακών ροών. Σημαντικά αποτελέσματα. Κατανοητή παρουσίαση.",
                                FR: "Simulation complexe des flux d'énergie. Résultats significatifs. Présentation compréhensible.",
                                NL: "Complexe simulatie van energiestromen. Betekenisvolle resultaten. Begrijpelijke presentatie.",
                                EN: "Complex simulation of energy flows. Meaningful results. Understandable presentation.",
                            })}
                        </div>
                        <div className="space-y-1">
                            <Bullet
                                icon={faChevronCircleRight}
                                className="text-sm"
                                twIconClassName="text-reonic-yellow"
                                title={languageSwitch(lang, {
                                    // Sustainable: Integrate profitability and emissions estimates.
                                    ET: "Jätkusuutlik: Kasumlikkuse ja heitkoguste hinnangute integreerimine.",
                                    LT: "Tvarus: Integruokite pelningumo ir išmetamųjų teršalų įverčius.",
                                    LV: "Ilgtspējīgs: Integrēt rentabilitātes un emisiju aplēses.",
                                    ES: "Sostenible: integración de estimaciones de rentabilidad y emisiones.",
                                    TR: "Sürdürülebilir: Kârlılık ve emisyon tahminlerini entegre edin.",
                                    CZ: "Udržitelné: Integrovat odhady ziskovosti a emisí.",
                                    EL: "Βιώσιμα: Ολοκληρώστε τις εκτιμήσεις κερδοφορίας και εκπομπών.",
                                    FR: "Durable : Intégrer la rentabilité et les estimations d'émissions.",
                                    NL: "Duurzaam: Integreer schattingen van winstgevendheid en emissies.",
                                    EN: "Sustainable: Integrate profitability and emissions estimates.",
                                })}
                            />
                            <Bullet
                                icon={faChevronCircleRight}
                                className="text-sm"
                                twIconClassName="text-reonic-yellow"
                                title={languageSwitch(lang, {
                                    // Graphics: Illustrate the results in an attractive way and make them easy to understand.
                                    ET: "Graafika: Illustreerige tulemusi atraktiivselt ja tehke need kergesti arusaadavaks.",
                                    LT: "Grafika: Grafika: patraukliai iliustruokite rezultatus ir užtikrinkite, kad jie būtų lengvai suprantami.",
                                    LV: "Grafika: Ilustrējiet rezultātus pievilcīgā veidā un padariet tos viegli saprotamus.",
                                    ES: "Gráficos: presente los resultados de forma atractiva asegurándose de que sean fáciles de entender.",
                                    TR: "Grafikler: Sonuçları çekici bir şekilde gösterin ve anlaşılmasını kolaylaştırın.",
                                    CZ: "Grafika: Znázorněte výsledky atraktivním způsobem, aby byly snadno pochopitelné.",
                                    EL: "Γραφική απεικόνιση: Παρουσιάστε τα αποτελέσματα με ελκυστικό τρόπο και κάντε τα εύκολα κατανοητά.",
                                    FR: "Graphiques : Illustrer les résultats de manière attrayante et les rendre faciles à comprendre.",
                                    NL: "Grafieken: Illustreer de resultaten op een aantrekkelijke manier en maak ze gemakkelijk te begrijpen.",
                                    EN: "Graphics: Present the results in a visually engaging manner, ensuring they are clear and easy to understand.",
                                })}
                            />
                            <Bullet
                                icon={faChevronCircleRight}
                                className="text-sm"
                                twIconClassName="text-reonic-yellow"
                                title={languageSwitch(lang, {
                                    // Exportable: Download graphics or a PDF report.
                                    ET: "Eksporditav: Graafika või PDF-aruande allalaadimine.",
                                    LT: "Galima eksportuoti: Atsisiųsti grafiką arba PDF ataskaitą.",
                                    LV: "Eksportējams: Lejupielādēt grafiku vai PDF pārskatu.",
                                    ES: "Exportable: descarga de gráficos o de un informe en PDF.",
                                    TR: "Dışa aktarılabilir: Grafikleri veya PDF raporunu indirin.",
                                    CZ: "Možnost exportu: Stáhněte si grafiku nebo zprávu ve formátu PDF.",
                                    EL: "Εξαγωγή δεδομένων: Λήψη γραφικών ή μιας έκθεσης σε μορφή αρχείου PDF.",
                                    FR: "Exportable : Télécharger des graphiques ou un rapport PDF.",
                                    NL: "Exporteren: Download afbeeldingen of een PDF-rapport.",
                                    EN: "Exportable: Download graphics or a PDF report.",
                                })}
                            />
                        </div>
                    </div>
                    <Img
                        size="xl"
                        alt="" //
                        className="w-full xl:w-[150%]"
                        src="/mirror/landing_page_expansion/Int - Simulate.png"
                    />
                </Container>
            </Section>
            <Section gray className="py-44">
                <Img
                    size="xl"
                    alt="" //
                    className="w-[calc(100%+3%/2)] -mx-[3%]"
                    src="/pages/fr-fr/industry/solar/Step 5 - Pdfs.png"
                />
                <div className="mt-10">
                    <div className="text-d-5xl font-bold">
                        {languageSwitch(lang, {
                            // Create the best looking offer, ever.
                            ET: "Looge kõige paremini välja nägev pakkumine, kunagi.",
                            LT: "Sukurkite geriausiai atrodantį pasiūlymą.",
                            LV: "Izveidojiet visskaistāko piedāvājumu.",
                            ES: "Cree la oferta más atractiva.",
                            TR: "Şimdiye kadarki en iyi görünen teklifi oluşturun.",
                            CZ: "Vytvořte nejlépe vypadající nabídku vůbec.",
                            EL: "Δημιουργήστε την πιο αισθητικά ολοκληρωμένη προσφορά που κάνατε ποτέ.",
                            FR: "Créez l'offre la plus attrayante qui soit.",
                            NL: "Maak de best uitziende aanbieding ooit.",
                            EN: "Create the best looking offer, ever.",
                        })}
                    </div>
                    <div className="text-xl mb-5 mt-1">
                        {languageSwitch(lang, {
                            // Renewable energy is a great business, but your competitors aren't sleeping. Convince customers with the best-looking, most well-explained, and clearest PDF offer.
                            ET: "Taastuvenergia on suurepärane äri, kuid teie konkurendid ei maga. Veenda kliente kõige parema väljanägemisega, kõige paremini selgitatud ja kõige selgemate PDF-pakkumistega.",
                            LT: "Atsinaujinančioji energija yra puikus verslas, tačiau jūsų konkurentai nemiega. Įtikinkite klientus pateikdami geriausiai atrodantį, geriausiai paaiškintą ir aiškiausią PDF pasiūlymą.",
                            LV: "Atjaunojamā enerģija ir lielisks bizness, taču jūsu konkurenti neguļ. Pārlieciniet klientus ar visskaidrāko, visskaidrāko un skaidrāko PDF piedāvājumu.",
                            ES: "La energía renovable es un gran negocio, pero su competencia no duerme. Convenza a los clientes con la oferta en PDF más atrayente, mejor explicada y más clara.",
                            TR: "Yenilenebilir enerji harika bir iş, ancak rakipleriniz uyumuyor. En iyi görünen, en iyi açıklanmış ve en net PDF teklifi ile müşterileri ikna edin.",
                            CZ: "Obnovitelná energie je skvělý byznys, ale vaši konkurenti nespí. Přesvědčte zákazníky nejlépe vypadající, nejlépe vysvětlenou a nejpřehlednější nabídkou ve formátu PDF.",
                            EL: "Οι ανανεώσιμες πηγές ενέργειας είναι μια σπουδαία επιχείρηση, αλλά οι ανταγωνιστές σας δεν κοιμούνται. Πείστε τους πελάτες με την αισθητική αρτιότητα, την πληρότητα και τη κατανοητή σαφήνεια της προσφοράς σας σε μορφή αρχείου PDF.",
                            FR: "L’énergie renouvelable est une opportunité en or — mais la concurrence est féroce. Démarquez-vous avec une offre PDF claire, esthétique et convaincante, pour séduire vos clients dès le premier regard.",
                            NL: "Duurzame energie is een geweldige business, maar je concurrenten slapen niet. Overtuig klanten met het mooiste, best uitgelegde en duidelijkste PDF-aanbod.",
                            EN: "Renewable energy is a great business, but your competitors aren't sleeping. Convince customers with the best-looking, most well-explained, and clearest PDF offer.",
                        })}
                    </div>
                    <div className="space-y-1">
                        <Bullet
                            icon={faChevronCircleRight}
                            className="text-sm"
                            title={languageSwitch(lang, {
                                // Adjustable to your CI, including logos, colors, and content.
                                ET: "Kohandatav teie CI-le, sealhulgas logod, värvid ja sisu.",
                                LT: "Pritaikoma jūsų CI, įskaitant logotipus, spalvas ir turinį.",
                                LV: "Pielāgojami jūsu CI, tostarp logotipi, krāsas un saturs.",
                                ES: "Ajustable a su CI, incluidos logotipos, colores y contenido.",
                                TR: "Logolar, renkler ve içerik dahil olmak üzere CI'nıza göre ayarlanabilir.",
                                CZ: "Logolar, renkler ve içerik dahil olmak üzere CI'nıza göre ayarlanabilir.",
                                EL: "Δυνατότητα προσαρμογής στην εταιρική σας ταυτότητα, συμπεριλαμβανομένων των λογότυπων, των χρωμάτων και του περιεχομένου.",
                                FR: "Personnalisez à votre image : logos, couleurs, contenus — tout s’adapte à votre identité.",
                                NL: "Aanpasbaar aan je CI, inclusief logo's, kleuren en inhoud.",
                                EN: "Adjustable to your corporate branding, including logos, colors, and content.",
                            })}
                        />
                        <Bullet
                            icon={faChevronCircleRight}
                            className="text-sm"
                            title={languageSwitch(lang, {
                                // Detailed explanations on rentability and ecologic impact, all with one click.
                                ET: "Üksikasjalikud selgitused üüritavuse ja ökoloogilise mõju kohta, kõik ühe klõpsuga.",
                                LT: "Išsamūs paaiškinimai apie nuomos galimybes ir poveikį aplinkai - viskas vienu paspaudimu.",
                                LV: "Detalizēti paskaidrojumi par īres iespējām un ietekmi uz vidi - ar vienu klikšķi.",
                                ES: "Explicaciones detalladas sobre la rentabilidad y el impacto ecológico, todo con un solo clic.",
                                TR: "Kiralanabilirlik ve ekolojik etki hakkında ayrıntılı açıklamalar, hepsi tek bir tıklamayla.",
                                CZ: "Kiralanabilirlik ve ekolojik etki hakkında ayrıntılı açıklamalar, hepsi tek bir tıklamayla.",
                                EL: "Λεπτομερής ανάλυση της απόδοσης και του οικολογικού οφέλους, όλα με ένα κλικ.",
                                FR: "Rendez l'offre lisible et persuasive : mettez en avant la rentabilité et l’impact écologique en toute transparence.",
                                NL: "Gedetailleerde uitleg over verhuurbaarheid en ecologische impact, alles met één klik.",
                                EN: "Detailed explanations of rentability and environmental impact, all at the click of a button.",
                            })}
                        />
                        <Bullet
                            icon={faChevronCircleRight}
                            className="text-sm"
                            title={languageSwitch(lang, {
                                // Clean, professional look."
                                ET: "Puhas, professionaalne välimus.",
                                LT: "Švari, profesionali išvaizda.",
                                LV: "Tīrs, profesionāls izskats.",
                                ES: "Aspecto limpio y profesional.",
                                TR: "Temiz, profesyonel görünüm.",
                                CZ: "Temiz, profesyonel görünüm.",
                                EL: "Σοβαρή, επαγγελματική εικόνα.",
                                FR: "Soignez la présentation : un rendu propre, professionnel et immédiat.",
                                NL: "Strakke, professionele uitstraling.",
                                EN: "Clean, professional look.",
                            })}
                        />
                    </div>
                </div>
                <div className="h-36" />
                <Container mode="image-top" xlMode="image-right">
                    <div>
                        <div className="text-d-5xl font-bold">
                            {languageSwitch(lang, {
                                // Or use our digital offer.
                                ET: "Või kasutage meie digitaalset pakkumist.",
                                LT: "Arba pasinaudokite mūsų skaitmeniniu pasiūlymu.",
                                LV: "Vai arī izmantojiet mūsu digitālo piedāvājumu.",
                                ES: "O utilice nuestra oferta digital.",
                                TR: "Ya da dijital teklifimizi kullanın.",
                                CZ: "Ya da dijital teklifimizi kullanın.",
                                EL: "Ή χρησιμοποιήστε την ψηφιακή μας λύση.",
                                FR: "Ou utilisez notre offre numérique.",
                                NL: "Of maak gebruik van ons digitale aanbod.",
                                EN: "Or use our digital offer.",
                            })}
                        </div>
                        <div className="text-xl mb-5 mt-1">
                            {languageSwitch(lang, {
                                // Want to stand out? Use our digital offer and send your offer straight to the top of your customer's inbox.
                                ET: "Tahad välja paista? Kasutage meie digitaalset pakkumist ja saatke oma pakkumine otse kliendi postkasti.",
                                LT: "Norite išsiskirti? Naudokitės mūsų skaitmeniniu pasiūlymu ir siųskite savo pasiūlymą tiesiai į klientų pašto dėžutės viršų.",
                                LV: "Vēlaties izcelties? Izmantojiet mūsu digitālo piedāvājumu un nosūtiet savu piedāvājumu uzreiz uz jūsu klienta pastkastītes augšdaļu.",
                                ES: "¿Quiere destacar? Utilice nuestra oferta digital y envíe su oferta directamente a la bandeja de entrada de sus clientes.",
                                TR: "Öne çıkmak mı istiyorsunuz? Dijital teklifimizi kullanın ve teklifinizi doğrudan müşterinizin gelen kutusunun en üstüne gönderin.",
                                CZ: "Öne çıkmak mı istiyorsunuz? Dijital teklifimizi kullanın ve teklifinizi doğrudan müşterinizin gelen kutusunun en üstüne gönderin.",
                                EL: "Θέλετε να ξεχωρίσετε; Χρησιμοποιήστε την ψηφιακή μας λύση και στείλτε την προσφορά σας κατευθείαν στην κορυφή των εισερχομένων μηνυμάτων του πελάτη σας.",
                                FR: "Vous voulez vous démarquer ? Utilisez notre offre numérique et envoyez votre offre directement en tête de la boîte de réception de vos clients.",
                                NL: "Wil je opvallen? Gebruik ons digitale aanbod en stuur je aanbod rechtstreeks naar de bovenkant van de inbox van je klant.",
                                EN: "Want to stand out? Use our digital offer and send it straight to the top of your customer's inbox.",
                            })}
                        </div>
                        <div className="space-y-1">
                            <Bullet
                                icon={faChevronCircleRight}
                                className="text-sm"
                                title={languageSwitch(lang, {
                                    // 100% digital experience
                                    ET: "100% digitaalne kogemus",
                                    LT: "100 % skaitmeninė patirtis",
                                    LV: "100% digitālā pieredze",
                                    ES: "100% experiencia digital",
                                    TR: "100 dijital deneyim",
                                    CZ: "100% digitální zkušenost",
                                    EL: "100% ψηφιακή εμπειρία",
                                    FR: "Une expérience 100% numérique",
                                    NL: "100% digitale ervaring",
                                    EN: "100% digital experience",
                                })}
                            />
                            <Bullet
                                icon={faChevronCircleRight}
                                className="text-sm"
                                title={languageSwitch(lang, {
                                    // Digital signature included
                                    ET: "Digitaalne allkiri lisatud",
                                    LT: "Įtrauktas skaitmeninis parašas",
                                    LV: "Iekļauts digitālais paraksts",
                                    ES: "Firma digital incluida",
                                    TR: "Dijital imza dahildir",
                                    CZ: "Včetně digitálního podpisu",
                                    EL: "Περιλαμβάνει ψηφιακή υπογραφή",
                                    FR: "Signature numérique incluse",
                                    NL: "Digitale handtekening inbegrepen",
                                    EN: "Digital signature included",
                                })}
                            />
                            <Bullet
                                icon={faChevronCircleRight}
                                className="text-sm"
                                title={languageSwitch(lang, {
                                    // Desktop- & Mobile-friendly experience
                                    ET: "Töölaua- ja mobiilisõbralik kogemus",
                                    LT: "Kompiuteriui ir mobiliesiems įrenginiams pritaikyta patirtis",
                                    LV: "Darbvirsmas un mobilajām ierīcēm draudzīga pieredze",
                                    ES: "Experiencia para ordenadores de sobremesa y móviles",
                                    TR: "Masaüstü ve Mobil uyumlu deneyim",
                                    CZ: "Zkušenosti pro stolní počítače a mobilní zařízení",
                                    EL: "Εύχρηστη σε περιβάλλον υπολογιστών και κινητών",
                                    FR: "Une expérience adaptée aux ordinateurs de bureau et aux téléphones portables",
                                    NL: "Geschikt voor desktop en mobiel",
                                    EN: "Desktop & mobile friendly experience",
                                })}
                            />
                        </div>
                    </div>
                    <Img
                        size="xl"
                        alt="" //
                        className="w-full sm:w-[130%]"
                        src="/mirror/landing_page_expansion/Int - Digital Offer.png"
                    />
                </Container>
            </Section>
            <Section className="py-24 sm:py-36 flex flex-col gap-14" outerClassName="bg-black/90">
                <div>
                    <div className="text-sm font-medium mb-5 text-reonic-yellow">
                        {languageSwitch(lang, {
                            // What are you waiting for?
                            ET: "Mida sa ootad?",
                            LT: "Ko laukiate?",
                            LV: "Ko tu vēl gaidi?",
                            ES: "¿A qué espera?",
                            TR: "Ne bekliyorsun?",
                            CZ: "Na co čekáte?",
                            EL: "Τι περιμένετε;",
                            FR: "Qu'attendez-vous ?",
                            NL: "Waar wacht je nog op?",
                            EN: "What are you waiting for?",
                        })}
                    </div>
                    <div className="font-bold text-white text-d-5xl">
                        {languageSwitch(lang, {
                            // Reonic is the best solution.
                            ET: "Reonic on parim lahendus.",
                            LT: "Reonic yra geriausias sprendimas.",
                            LV: "Reonic ir labākais risinājums.",
                            ES: "Reonic es la mejor solución.",
                            TR: "Reonic en iyi çözümdür.",
                            CZ: "Reonic je nejlepší řešení.",
                            EL: "Το Reonic είναι η καλύτερη λύση.",
                            FR: "Reonic est la meilleure solution.",
                            NL: "Reonic is de beste oplossing.",
                            EN: "Reonic is the best solution.",
                        })}
                    </div>
                    <div className="font-bold text-white text-d-5xl mt-1">
                        {languageSwitch(lang, {
                            ET: "Seda ütlevad meie üle 1000 kliendi.",
                            LT: "Taip teigia daugiau nei 1000 mūsų klientų.",
                            LV: "Tā apgalvo mūsu vairāk nekā 1000 klientu.",
                            ES: "Más de 1000 clientes dicen todo esto de nuestra empresa.",
                            TR: "1000'den fazla müşterimiz böyle söylüyor.",
                            CZ: "To říká více než 1000 našich zákazníků.",
                            EL: "Αυτό λένε οι 1000+ πελάτες μας.",
                            FR: "C'est ce que disent nos 1000 clients.",
                            NL: "Dat zeggen onze meer dan 1000 klanten.",
                            EN: "That's what our 1000+ customers say.",
                        })}
                    </div>
                </div>

                <div className="border-l-2 border-reonic-yellow pl-10">
                    <div className="-mt-1 font-normal text-lg text-white">
                        {languageSwitch(lang, {
                            // Reonic is a state-of-the-art tool for photovoltaic planning - world-class support and performance. It is clearly better than comparable suppliers on the the market. We're delighted!
                            ET: "Reonic on fotogalvaanilise planeerimise tipptasemel vahend - maailmatasemel tugi ja jõudlus. See on selgelt parem kui võrreldavad tarnijad turul. Oleme väga rahul!",
                            LT: "Reonic yra naujausia fotovoltinio planavimo priemonė - pasaulinio lygio palaikymas ir našumas. Ji akivaizdžiai pranašesnė už panašius rinkos tiekėjus. Esame sužavėti!",
                            LV: "Reonic ir mūsdienīgs fotogalvaniskās plānošanas rīks - pasaules klases atbalsts un veiktspēja. Tas ir nepārprotami labāks par tirgū pieejamajiem salīdzināmajiem piegādātājiem. Mēs esam sajūsmā!",
                            ES: "Reonic es una herramienta de última generación para la planificación fotovoltaica: asistencia y rendimiento de primera clase. Es claramente mejor que otros proveedores comparables del mercado. Estamos encantados.",
                            TR: "Reonic, fotovoltaik planlama için son teknoloji ürünü bir araçtır - dünya standartlarında destek ve performans. Piyasadaki benzer tedarikçilerden açıkça daha iyi. Çok memnunuz!",
                            CZ: "Reonic je nejmodernější nástroj pro fotovoltaické plánování - prvotřídní podpora a výkon. Je jednoznačně lepší než srovnatelní dodavatelé na trhu. Jsme nadšeni!",
                            EL: "Το Reonic είναι ένα υπερσύγχρονο εργαλείο για τον φωτοβολταϊκό σχεδιασμό - υποστήριξη και απόδοση παγκόσμιας κλάσης. Είναι σαφώς καλύτερο από συγκρίσιμους προμηθευτές της αγοράς. Είμαστε ενθουσιασμένοι!",
                            FR: "Reonic est un outil de pointe pour la planification photovoltaïque - un support et une performance de classe mondiale. Il est nettement meilleur que les fournisseurs comparables sur le marché. Nous sommes ravis !",
                            NL: "Reonic is een geavanceerd hulpmiddel voor fotovoltaïsche planning - ondersteuning en prestaties van wereldklasse. Het is duidelijk beter dan vergelijkbare leveranciers op de markt. We zijn verheugd!",
                            EN: "Reonic is a state-of-the-art tool for photovoltaic planning - world-class support and performance. It is clearly better than comparable suppliers on the the market. We're delighted!",
                        })}
                    </div>
                    <div className="flex gap-3 items-center mt-4">
                        <div className="rounded-full overflow-hidden w-12 aspect-square">
                            <Img
                                size="xxs"
                                alt=""
                                className="w-full h-full object-cover"
                                src={ENDORSEMENTS[2].imageUrlPerson}
                            />
                        </div>

                        <div className="text-white">
                            <div className="font-medium text-sm">Christoph</div>
                            <div className="text-white/75 text-xs">CEO — D,5 Energie</div>
                        </div>
                    </div>
                </div>
            </Section>
            <Section className="py-44">
                <Img
                    size="xl"
                    alt="" //
                    className="w-[calc(100%+2*7%)] -mx-[7%]"
                    src="/mirror/landing_page_expansion/Int - Projekt Management.png"
                />
                <div className="mt-5">
                    <div className="text-sm font-medium mb-3 text-reonic-red">
                        {languageSwitch(lang, {
                            // Step 3
                            ET: "3. samm",
                            LT: "3 žingsnis",
                            LV: "3. solis",
                            ES: "Paso 3",
                            TR: "Adım 3",
                            CZ: "Krok 3",
                            EL: "Βήμα 3",
                            FR: "Étape 3",
                            NL: "Stap 3",
                            EN: "Step 3",
                        })}
                    </div>
                    <div className="text-d-5xl font-bold">
                        {languageSwitch(lang, {
                            // With project management, user & team assignments, tasks, notes, files & more.
                            ET: "Projektijuhtimine, kasutajate ja meeskonna ülesanded, ülesanded, märkmed, failid ja palju muud.",
                            LT: "Su projektų valdymu, naudotojų ir komandų užduotimis, užduotimis, pastabomis, failais ir dar daugiau.",
                            LV: "Ar projektu pārvaldību, lietotāju un komandu uzdevumiem, uzdevumiem, piezīmēm, failiem un daudz ko citu.",
                            ES: "Con gestión de proyectos, asignaciones de usuarios y equipos, tareas, notas, archivos y mucho más.",
                            TR: "Proje yönetimi, kullanıcı ve ekip atamaları, görevler, notlar, dosyalar ve daha fazlası ile.",
                            CZ: "Správa projektů, přiřazení uživatelů a týmů, úkoly, poznámky, soubory a další.",
                            EL: "Με διαχείριση έργων, αναθέσεις χρηστών και ομάδων, εργασίες, σημειώσεις, αρχεία και πολλά άλλα.",
                            FR: "Gestion de projets, affectations d'utilisateurs et d'équipes, tâches, notes, fichiers, et bien plus encore…",
                            NL: "Met projectbeheer, gebruikers- en teamtoewijzingen, taken, notities, bestanden en meer.",
                            EN: "With project management, user & team assignments, tasks, notes, files & more.",
                        })}
                    </div>
                    <div className="text-xl mb-5 mt-2">
                        {languageSwitch(lang, {
                            // Are you using four, five or six different tools in your workflow? With Reonic, you have all you need in one single software. From first request to finished project.
                            ET: "Kas te kasutate oma tööprotsessis nelja, viit või kuut erinevat tööriista? Reoniciga on kõik vajalik ühes tarkvaras. Alates esimesest taotlusest kuni valmis projektini.",
                            LT: "Ar darbo eigoje naudojate keturis, penkis ar šešis skirtingus įrankius? Naudodami Reonic turite viską, ko jums reikia, vienoje programoje. Nuo pirmos užklausos iki baigto projekto.",
                            LV: "Vai savā darbplūsmā izmantojat četrus, piecus vai sešus dažādus rīkus? Ar Reonic jums ir viss nepieciešamais vienā programmatūrā. No pirmā pieprasījuma līdz pabeigtam projektam.",
                            ES: "¿Utiliza cuatro, cinco o seis herramientas diferentes en su flujo de trabajo? Con Reonic, tiene todo lo que necesita en un único software. Desde la primera solicitud hasta el proyecto terminado.",
                            TR: "İş akışınızda dört, beş veya altı farklı araç mı kullanıyorsunuz? Reonic ile ihtiyacınız olan her şeye tek bir yazılımda sahip olursunuz. İlk talepten bitmiş projeye kadar.",
                            CZ: "Používáte ve svém pracovním postupu čtyři, pět nebo šest různých nástrojů? S Reonicem máte vše, co potřebujete, v jediném softwaru. Od prvního požadavku až po hotový projekt.",
                            EL: "Χρησιμοποιείτε τέσσερα, πέντε ή έξι διαφορετικά εργαλεία στη ροή εργασιών σας; Με το Reonic, έχετε όλα όσα χρειάζεστε σε ένα μόνο λογισμικό. Από το πρώτο αίτημα εκδήλωσης ενδιαφέροντος μέχρι το τελικό έργο.",
                            FR: "Utilisez-vous quatre, cinq ou six outils différents pour gérer votre flux de travail ? Avec Reonic, vous disposez de tout ce dont vous avez besoin dans un seul logiciel. De la première demande jusqu’au projet final.",
                            NL: "Gebruik je vier, vijf of zes verschillende tools in je workflow? Met Reonic heb je alles wat je nodig hebt in één software. Van de eerste aanvraag tot het voltooide project.",
                            EN: "Are you using four, five or six different tools in your workflow? With Reonic, you have all you need in one single software. From first request to finished project.",
                        })}
                    </div>
                    <div className="space-y-1">
                        <Bullet
                            icon={faChevronCircleRight}
                            className="text-sm"
                            twIconClassName="text-reonic-red"
                            title={languageSwitch(lang, {
                                // Detailed user-roles and access policies, including for external sales staff
                                ET: "Üksikasjalikud kasutajarollid ja juurdepääsupoliitikad, sealhulgas välismüügipersonalile",
                                LT: "Išsamios naudotojų funkcijos ir prieigos politika, įskaitant išorinių pardavimų darbuotojų",
                                LV: "Detalizētas lietotāju lomas un piekļuves politikas, tostarp ārējiem tirdzniecības darbiniekiem",
                                ES: "Funciones de usuario y políticas de acceso detalladas, incluso para el personal de ventas externo.",
                                TR: "Dış satış personeli de dahil olmak üzere ayrıntılı kullanıcı rolleri ve erişim politikaları",
                                CZ: "Dış satış personeli de dahil olmak üzere ayrıntılı kullanıcı rolleri ve erişim politikaları",
                                EL: "Λεπτομερείς ρόλοι χρηστών και πολιτικές πρόσβασης, συμπεριλαμβανομένων των εξωτερικών πωλητών",
                                FR: "Définissez des rôles utilisateurs et des politiques d’accès précises, y compris pour les équipes commerciales.",
                                NL: "Gedetailleerde gebruikersrollen en toegangsbeleid, ook voor extern verkooppersoneel",
                                EN: "Detailed user-roles and access policies, including for external sales staff",
                            })}
                        />
                        <Bullet
                            icon={faChevronCircleRight}
                            className="text-sm"
                            twIconClassName="text-reonic-red"
                            title={languageSwitch(lang, {
                                // All your customer and project information in one place
                                ET: "Kogu kliendi- ja projektiteave ühes kohas",
                                LT: "Visa informacija apie klientus ir projektus vienoje vietoje",
                                LV: "Visa jūsu klientu un projektu informācija vienuviet",
                                ES: "Toda la información sobre clientes y proyectos en un solo lugar",
                                TR: "Tüm müşteri ve proje bilgileriniz tek bir yerde",
                                CZ: "Tüm müşteri ve proje bilgileriniz tek bir yerde",
                                EL: "Όλες οι πληροφορίες για τους πελάτες και τα έργα σας διαθέσιμες σε ένα σημείο",
                                FR: "Centralisez toutes les informations relatives aux clients et aux projets au même endroit.",
                                NL: "Alle klant- en projectinformatie op één plek",
                                EN: "All your customer and project information in one place",
                            })}
                        />
                        <Bullet
                            icon={faChevronCircleRight}
                            className="text-sm"
                            twIconClassName="text-reonic-red"
                            title={languageSwitch(lang, {
                                // Replaces 5+ different software tools.
                                ET: "Asendab 5+ erinevat tarkvara.",
                                LT: "Pakeičia daugiau nei 5 skirtingas programinės įrangos priemones.",
                                LV: "Aizstāj vairāk nekā 5 dažādus programmatūras rīkus.",
                                ES: "Sustituye a más de 5 herramientas de software diferentes.",
                                TR: "5+ farklı yazılım aracının yerini alır.",
                                CZ: "5+ farklı yazılım aracının yerini alır.",
                                EL: "Αντικαθιστά 5+ διαφορετικά εργαλεία λογισμικού.",
                                FR: "Remplacez plus de cinq outils logiciels différents grâce à une solution tout-en-un.",
                                NL: "Vervangt meer dan 5 verschillende softwaretools.",
                                EN: "Replaces 5+ different software tools.",
                            })}
                        />
                    </div>
                </div>
                <div className="mt-20" />
                <Container mode="image-top" xlMode="image-right">
                    <div>
                        <div className="text-d-3xl font-bold">
                            {languageSwitch(lang, {
                                // Calendar & E-Mail sync.
                                ET: "Kalendri ja e-posti sünkroonimine.",
                                LT: "Kalendoriaus ir el. pašto sinchronizavimas.",
                                LV: "Kalendāra un e-pasta sinhronizācija.",
                                ES: "Sincronización de calendario y correo electrónico.",
                                TR: "Takvim ve E-Posta senkronizasyonu.",
                                CZ: "Takvim ve E-Posta senkronizasyonu.",
                                EL: "Συγχρονισμός ημερολογίου & email.",
                                FR: "Calendar & E-Mail sync.",
                                NL: "Agenda en e-mail synchronisatie.",
                                EN: "Calendar & E-Mail sync.",
                            })}
                        </div>
                        <div className="text-xl mb-5 mt-1">
                            {languageSwitch(lang, {
                                // No more jumping between applications. Use your calendar & emails in Reonic.
                                ET: "Enam ei ole vaja hüpata rakenduste vahel. Kasutage oma kalendrit ja e-kirju Reonicis.",
                                LT: "Daugiau nebereikia šokinėti tarp programų. Naudokitės kalendoriumi ir el. laiškais Reonic.",
                                LV: "Nevajag vairs pārslēgties starp lietojumprogrammām. Izmantojiet savu kalendāru un e-pastus Reonic.",
                                ES: "No más saltos entre aplicaciones. Utilice su calendario y correos electrónicos en Reonic.",
                                TR: "Artık uygulamalar arasında atlamak yok. Takviminizi ve e-postalarınızı Reonic'te kullanın.",
                                CZ: "Artık uygulamalar arasında atlamak yok. Takviminizi ve e-postalarınızı Reonic'te kullanın.",
                                EL: "Δεν χρειάζεται πια να ανοίγετε διαφορετικές εφαρμογές. Χρησιμοποιήστε το ημερολόγιο και τα email σας στο Reonic.",
                                FR: "Plus besoin de passer d'une application à l'autre. Utilisez votre calendrier et vos e-mails dans Reonic.",
                                NL: "Niet meer schakelen tussen applicaties. Gebruik je agenda en e-mails in Reonic.",
                                EN: "No more jumping between applications. Use your calendar & emails in Reonic.",
                            })}
                        </div>
                        <div className="space-y-1">
                            <Bullet
                                twIconClassName="text-reonic-red"
                                icon={faChevronCircleRight}
                                className="text-sm"
                                title={languageSwitch(lang, {
                                    // 100% digital experience
                                    ET: "100% digitaalne kogemus",
                                    LT: "100 % skaitmeninė patirtis",
                                    LV: "100% digitālā pieredze",
                                    ES: "100% experiencia digital",
                                    TR: "100 dijital deneyim",
                                    CZ: "100% digitální zkušenost",
                                    EL: "Ενσωμάτωση ημερολογίου",
                                    FR: "Une expérience 100% numérique",
                                    NL: "Integraties voor Outlook en Gmail",
                                    EN: "Integrations for Outlook and Gmail",
                                })}
                            />
                            <Bullet
                                twIconClassName="text-reonic-red"
                                icon={faChevronCircleRight}
                                className="text-sm"
                                title={languageSwitch(lang, {
                                    // Digital signature included
                                    ET: "Digitaalne allkiri lisatud",
                                    LT: "Įtrauktas skaitmeninis parašas",
                                    LV: "Iekļauts digitālais paraksts",
                                    ES: "Firma digital incluida",
                                    TR: "Dijital imza dahildir",
                                    CZ: "Včetně digitálního podpisu",
                                    EL: "Ενσωμάτωση email",
                                    FR: "Signature numérique incluse",
                                    NL: "Alle informatie op één platform",
                                    EN: "All information on a single plattform",
                                })}
                            />
                            <Bullet
                                twIconClassName="text-reonic-red"
                                icon={faChevronCircleRight}
                                className="text-sm"
                                title={languageSwitch(lang, {
                                    // Desktop- & Mobile-friendly experience
                                    ET: "Töölaua- ja mobiilisõbralik kogemus",
                                    LT: "Kompiuteriui ir mobiliesiems įrenginiams pritaikyta patirtis",
                                    LV: "Darbvirsmas un mobilajām ierīcēm draudzīga pieredze",
                                    ES: "Experiencia para ordenadores de sobremesa y móviles",
                                    TR: "Masaüstü ve Mobil uyumlu deneyim",
                                    CZ: "Zkušenosti pro stolní počítače a mobilní zařízení",
                                    EL: "Εμπειρία φιλική προς το desktop και το κινητό",
                                    FR: "Une expérience adaptée aux ordinateurs de bureau et aux téléphones portables",
                                    NL: "Plan installatie- en verkoopbronnen",
                                    EN: "Schedule installation and sales resources",
                                })}
                            />
                        </div>
                    </div>
                    <Img
                        size="xl"
                        alt="" //
                        className="w-full sm:w-[130%]"
                        src="/mirror/landing_page_expansion/Int - CalendarsAndMail.png"
                    />
                </Container>
            </Section>
            <Pricing {...props} variants={props.variants} />
            <Waitlist {...props} variants={props.variants} />
            <Section className="grid grid-cols-4" />
        </>
    );
};

type Info = {
    title: string;
    headline: (f: number) => string;
    description: (f: number, a: number) => string;
};

const PRICE_MAP: Record<
    SupportedLocale,
    {
        light: Info;
        pro: Info;
        commercial: Info;
    }
> = {
    // Greece
    "EL-GR": {
        light: {
            title: "Light",
            headline: (f) => `${f}€/μήνα`,
            description: (f, a) =>
                `Περιλαμβάνεται ένας χρήστης. ${f} €/μήνα για τον δεύτερο χρήστη, ${a} €/μήνα για όλους τους επόμενους χρήστες.`,
        },
        pro: {
            title: "Pro",
            headline: (f) => `${f}€/μήνα`,
            description: (f, a) => `Περιλαμβάνονται δύο χρήστες. ${a} €/μήνα για όλους τους επόμενους χρήστες.`,
        },
        commercial: {
            title: "Commercial add-on",
            headline: (f) => `${f}€/μήνα`,
            description: (f, a) =>
                `Περιλαμβάνεται ένας χρήστης. ${f} €/μήνα για τον δεύτερο χρήστη, ${a} €/μήνα για όλους τους επόμενους χρήστες`,
        },
    },
    // Turkey
    "TR-TR": {
        light: {
            title: "Light",
            headline: (f) => `${f}$/month`,
            description: (f, a) =>
                `One user included. ${f}$/month for the second user, ${a}$/month for all users after.`,
        },
        pro: {
            title: "Pro",
            headline: (f) => `${f}$/month`,
            description: (f, a) => `Two users included. ${a}$/month for all users after.`,
        },
        commercial: {
            title: "Commercial add-on",
            headline: (f) => `${f}$/month`,
            description: (f, a) =>
                `One user included. ${f}$/month for the second user, ${a}$/month for all users after.`,
        },
    },
    // Czech Republic
    "CZ-CZ": {
        light: {
            title: "Light",
            headline: (f) => `${f}€/month`,
            description: (f, a) =>
                `One user included. ${f}€/month for the second user, ${a}€/month for all users after.`,
        },
        pro: {
            title: "Pro",
            headline: (f) => `${f}€/month`,
            description: (f, a) => `Two users included. ${a}€/month for all users after.`,
        },
        commercial: {
            title: "Commercial add-on",
            headline: (f) => `${f}€/month`,
            description: (f, a) =>
                `One user included. ${f}€/month for the second user, ${a}€/month for all users after.`,
        },
    },
    // Mexico
    "ES-MX": {
        light: {
            title: "Luz",
            headline: (f) => `$${f}/mes`,
            description: (f, a) =>
                `Un usuario incluido. $${f}/mes para el segundo usuario, $${a}/mes para todos los usuarios posteriores.`,
        },
        pro: {
            title: "Pro",
            headline: (f) => `$${f}/month`,
            description: (f, a) => `Dos usuarios incluidos. $${a}/mes para todos los usuarios posteriores.`,
        },
        commercial: {
            title: "Comercial",
            headline: (f) => `$${f}/month`,
            description: (f, a) =>
                `Un usuario incluido. $${f}/mes para el segundo usuario, $${a}/mes para todos los usuarios posteriores.`,
        },
    },
    "EN-GB": {
        light: {
            title: "Light",
            headline: (f) => `${f}€/month`,
            description: (f, a) =>
                `One user included. ${f}€/month for the second user, ${a}€/month for all users after.`,
        },
        pro: {
            title: "Pro",
            headline: (f) => `${f}€/month`,
            description: (f, a) =>
                `Two users included. ${f}€/month for the second user, ${a}€/month for all users after.`,
        },
        commercial: {
            title: "Commercial add-on",
            headline: (f) => `${f}€/month`,
            description: (f, a) =>
                `One user included. ${f}€/month for the second user, ${a}€/month for all users after.`,
        },
    },
    // TODO
    "ET-EE": {
        light: {
            title: "Light",
            headline: (f) => `${f}€/month`,
            description: (f, a) =>
                `One user included. ${f}€/month for the second user, ${a}€/month for all users after.`,
        },
        pro: {
            title: "Pro",
            headline: (f) => `${f}€/month`,
            description: (f, a) =>
                `Two users included. ${f}€/month for the second user, ${a}€/month for all users after.`,
        },
        commercial: {
            title: "Commercial add-on",
            headline: (f) => `${f}€/month`,
            description: (f, a) =>
                `One user included. ${f}€/month for the second user, ${a}€/month for all users after.`,
        },
    },
    // TODO
    "LT-LT": {
        light: {
            title: "Light",
            headline: (f) => `${f}€/month`,
            description: (f, a) =>
                `One user included. ${f}€/month for the second user, ${a}€/month for all users after.`,
        },
        pro: {
            title: "Pro",
            headline: (f) => `${f}€/month`,
            description: (f, a) =>
                `Two users included. ${f}€/month for the second user, ${a}€/month for all users after.`,
        },
        commercial: {
            title: "Commercial add-on",
            headline: (f) => `${f}€/month`,
            description: (f, a) =>
                `One user included. ${f}€/month for the second user, ${a}€/month for all users after.`,
        },
    },
    // TODO
    "LV-LV": {
        light: {
            title: "Light",
            headline: (f) => `${f}€/month`,
            description: (f, a) =>
                `One user included. ${f}€/month for the second user, ${a}€/month for all users after.`,
        },
        pro: {
            title: "Pro",
            headline: (f) => `${f}€/month`,
            description: (f, a) =>
                `Two users included. ${f}€/month for the second user, ${a}€/month for all users after.`,
        },
        commercial: {
            title: "Commercial add-on",
            headline: (f) => `${f}€/month`,
            description: (f, a) =>
                `One user included. ${f}€/month for the second user, ${a}€/month for all users after.`,
        },
    },
    "FR-CA": {
        light: {
            title: "Light",
            headline: (f) => `$${f}/month`,
            description: (f, a) => `Un utilisateur inclus. $${a}/mois pour chaque utilisateur supplémentaire.`,
        },
        pro: {
            title: "Pro",
            headline: (f) => `$${f}/month`,
            description: (f, a) => `Deux utilisateurs inclus. $${a}/mois pour chaque utilisateur supplémentaire.`,
        },
        commercial: {
            title: "Commercial Planning",
            headline: (f) => `$${f}/month`,
            description: (f, a) => `Un utilisateur inclus. $${a}/mois pour chaque utilisateur supplémentaire.`,
        },
    },
    "EN-CA": {
        light: {
            title: "Light",
            headline: (f) => `$${f}/month`,
            description: (f, a) => `One user included. $${a}/month for each additional user.`,
        },
        pro: {
            title: "Pro",
            headline: (f) => `$${f}/month`,
            description: (f, a) => `Two users included. $${a}/month for each additional user.`,
        },
        commercial: {
            title: "Commercial Planning",
            headline: (f) => `$${f}/month`,
            description: (f, a) => `One user included. $${a}/month for each additional user.`,
        },
    },
};

export const makePriceMap = ({ locale, variants }: { locale: SupportedLocale; variants: ABTestVariants }) => {
    const selected = PRICE_MAP[locale];

    const priceIndex = ABTestPriceVariants.indexOf(variants.price);
    const index = priceIndex !== -1 ? priceIndex : 0;
    const _onboardingIndex = ABTestPriceVariants.indexOf(variants.onboardingFee);
    const onboardingIndex = _onboardingIndex !== -1 ? _onboardingIndex : 0;

    const { b: baseLight, u: perUserLight } = PRICING[locale][index].light;
    const { b: basePro, u: perUserPro } = PRICING[locale][index].pro;
    const { b: baseCommercial, u: perUserCommercial } = PRICING[locale][index].commercial;

    const onboarding = OnboardingFee[locale][onboardingIndex];
    const secondUserLight = 1.5 * (basePro - baseLight);
    const secondUserCommmercial = baseCommercial / 2;

    return {
        light: {
            title: selected.light.title,
            priceBase: baseLight,
            pricePerUser: perUserLight,
            headline: selected.light.headline(baseLight),
            description: selected.light.description(secondUserLight, perUserLight),
        },
        pro: {
            title: selected.pro.title,
            priceBase: basePro,
            pricePerUser: perUserPro,
            headline: selected.pro.headline(basePro),
            description: selected.pro.description(basePro, perUserPro),
        },
        commercial: {
            title: selected.commercial.title,
            priceBase: baseCommercial,
            pricePerUser: perUserCommercial,
            headline: selected.commercial.headline(baseCommercial),
            description: selected.commercial.description(secondUserCommmercial, perUserCommercial),
        },
        onboarding: {
            text: "",
            price: onboarding,
        },
    };
};

const Pricing = (props: PageProps & { locale: SupportedLocale; variants: ABTestVariants }) => {
    const lang = LANGUAGE_FOR_LOCALE[props.locale];
    const prices = makePriceMap(props);
    return (
        <Section relative={false} outerClassName="pt-56 pb-52" gray id="prices">
            <div className="text-d-5xl font-bold text-black">
                {languageSwitch(lang, {
                    // Pricing
                    ET: "Hinnakujundus",
                    LT: "Kainodara",
                    LV: "Cenu noteikšana",
                    ES: "Precios",
                    TR: "Fiyatlandırma",
                    CZ: "Stanovení cen",
                    EL: "Τιμολόγηση",
                    FR: "Tarification",
                    NL: "Prijzen",
                    EN: "Pricing",
                })}
            </div>
            <div className="text-d-3xl font-medium mt-3 mb-8 text-gray-500">
                {languageSwitch(lang, {
                    // We believe in transparent pricing.
                    ET: "Me usume läbipaistvasse hinnakujundusse.",
                    LT: "Tikime skaidria kainodara.",
                    LV: "Mēs uzskatām, ka cenas ir pārredzamas.",
                    ES: "Creemos en la transparencia de precios.",
                    TR: "Şeffaf fiyatlandırmaya inanıyoruz.",
                    CZ: "Věříme v transparentní ceny.",
                    EL: "Πιστεύουμε στη διαφανή τιμολόγηση.",
                    FR: "Nous croyons en la transparence des prix.",
                    NL: "Wij geloven in transparante prijzen.",
                    EN: "We believe in transparent pricing.",
                })}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-10">
                <div className="bg-white p-10 flex flex-col">
                    <div className="font-bold text-3xl">{prices.light.title}</div>
                    <div className="font-semibold text-3xl text-gray-500 mb-5">{prices.light.headline}</div>
                    <div className="font-semibold text-sm text-black mb-5">{prices.light.description}</div>

                    <Bullet
                        icon={faCheckCircle}
                        title={languageSwitch(lang, {
                            EN: "Lead-gen tool",
                            ET: "Lead-gen tööriist",
                            LT: "Lead-gen įrankis",
                            LV: "Lead-gen rīks",
                            ES: "Herramienta de lead-gen",
                            TR: "Lead-gen aracı",
                            CZ: "Lead-gen nástroj",
                            EL: "Εργαλείο Lead generation (δημιουργίας δυνητικών πελατών)",
                            FR: "Outil de génération de leads",
                            NL: "Lead-gen tool",
                        })}
                    />
                    <Bullet
                        icon={faCheckCircle}
                        title={languageSwitch(lang, {
                            EN: "Planning tool",
                            ET: "Planeerimisvahend",
                            LT: "Planavimo priemonė",
                            LV: "Plānošanas rīks",
                            ES: "Herramienta de planificación",
                            TR: "Planlama aracı",
                            CZ: "Plánovací nástroj",
                            EL: "Εργαλείο σχεδιασμού",
                            FR: "Outil de planification",
                            NL: "Hulpmiddel voor planning",
                        })}
                    />
                    <Bullet
                        icon={faCheckCircle}
                        title={languageSwitch(lang, {
                            EN: "Files, Notes, Tasks & more",
                            ET: "Failid, märkmed, ülesanded ja muud",
                            LT: "Failai, pastabos, užduotys ir dar daugiau",
                            LV: "Faili, piezīmes, uzdevumi un citi",
                            ES: "Archivos, notas, tareas y mucho más",
                            TR: "Dosyalar, Notlar, Görevler ve daha fazlası",
                            CZ: "Soubory, poznámky, úkoly a další",
                            EL: "Αρχεία, Σημειώσεις, Εργασίες & περισσότερα",
                            FR: "Fichiers, notes, tâches et plus encore",
                            NL: "Bestanden, notities, taken en meer",
                        })}
                    />
                    <Bullet
                        icon={faCheckCircle}
                        title={languageSwitch(lang, {
                            EN: "Project Management",
                            ET: "Projektijuhtimine",
                            LT: "Projektų valdymas",
                            LV: "Projektu vadība",
                            ES: "Gestión de proyectos",
                            TR: "Proje Yönetimi",
                            CZ: "Řízení projektů",
                            EL: "Διαχείριση έργων",
                            FR: "Gestion de projet",
                            NL: "Projectbeheer",
                        })}
                    />
                    <Bullet
                        icon={faCheckCircle}
                        title={languageSwitch(lang, {
                            EN: "Calendar integration",
                            ET: "Kalendri integreerimine",
                            LT: "Kalendoriaus integracija",
                            LV: "Kalendāra integrācija",
                            ES: "Integración del calendario",
                            TR: "Takvim entegrasyonu",
                            CZ: "Integrace kalendáře",
                            EL: "Ενσωμάτωση ημερολογίου",
                            FR: "Intégration du calendrier",
                            NL: "Kalenderintegratie",
                        })}
                    />
                    <Bullet
                        icon={faCheckCircle}
                        title={languageSwitch(lang, {
                            EN: "E-Mail integration",
                            ET: "E-posti integreerimine",
                            LT: "El. pašto integracija",
                            LV: "E-pasta integrācija",
                            ES: "Integración del correo electrónico",
                            TR: "E-Posta entegrasyonu",
                            CZ: "Integrace e-mailu",
                            EL: "Ενσωμάτωση email",
                            FR: "Intégration du courrier électronique",
                            NL: "E-mail integratie",
                        })}
                    />
                </div>
                <div className="bg-white p-10 flex flex-col">
                    <div className="font-bold text-3xl">{prices.pro.title}</div>
                    <div className="font-semibold text-3xl text-gray-500 mb-5">{prices.pro.headline}</div>
                    <div className="font-semibold text-sm text-black mb-5">{prices.pro.description}</div>

                    <Bullet
                        icon={faCheckCircle}
                        title={languageSwitch(lang, {
                            EN: "All features that are included in light",
                            ET: "Kõik funktsioonid, mis sisalduvad valguse",
                            LT: "Visos funkcijos, kurios yra įtrauktos į šviesos",
                            LV: "Visas funkcijas, kas iekļautas gaismas",
                            ES: "Todas las funciones incorporadas",
                            TR: "Işığa dahil olan tüm özellikler",
                            CZ: "Všechny funkce, které jsou zahrnuty v light",
                            EL: "Όλα τα χαρακτηριστικά που περιλαμβάνονται στην έκδοση Light",
                            FR: "Toutes les fonctionnalités incluses dans la lumière",
                            NL: "Alle functies die zijn inbegrepen in light",
                        })}
                    />
                    <Bullet
                        icon={faCheckCircle}
                        title={languageSwitch(lang, {
                            EN: "Teams feature",
                            ET: "Meeskondade funktsioon",
                            LT: "Komandų funkcija",
                            LV: "Komandu funkcija",
                            ES: "Equipos",
                            TR: "Ekipler özelliği",
                            CZ: "Funkce týmů",
                            EL: "Λειτουργία ομάδων",
                            FR: "Caractéristiques des équipes",
                            NL: "Teams",
                        })}
                    />
                    <Bullet
                        icon={faCheckCircle}
                        title={languageSwitch(lang, {
                            EN: "Multiple pipelines",
                            ET: "Mitu torustikku",
                            LT: "Keli vamzdynai",
                            LV: "Vairāki cauruļvadi",
                            ES: "Varias canalizaciones",
                            TR: "Çoklu boru hatları",
                            CZ: "Více potrubí",
                            EL: "Πολλαπλές γραμμές επικοινωνίας",
                            FR: "Pipelines multiples",
                            NL: "Meerdere pijplijnen",
                        })}
                    />
                    <Bullet
                        icon={faCheckCircle}
                        title={languageSwitch(lang, {
                            EN: "Guest users",
                            ET: "Külaliskasutajad",
                            LT: "Svečių naudotojai",
                            LV: "Viesu lietotāji",
                            ES: "Usuarios invitados",
                            TR: "Misafir kullanıcılar",
                            CZ: "Uživatelé z řad hostů",
                            EL: "Επισκέπτες χρήστες",
                            FR: "Utilisateurs invités",
                            NL: "Gastgebruikers",
                        })}
                    />
                    <Bullet
                        icon={faCheckCircle}
                        title={languageSwitch(lang, {
                            EN: "Shared calendars & scheduling assistant",
                            ET: "Jagatud kalendrid ja ajakava assistent",
                            LT: "Bendrai naudojami kalendoriai ir planavimo asistentas",
                            LV: "Koplietoti kalendāri un plānošanas palīgs",
                            ES: "Calendarios compartidos y asistente de programación",
                            TR: "Paylaşılan takvimler ve zamanlama asistanı",
                            CZ: "Sdílené kalendáře a asistent plánování",
                            EL: "Κοινόχρηστα ημερολόγια & βοηθός προγραμματισμού",
                            FR: "Calendriers partagés et assistant de planification",
                            NL: "Gedeelde kalenders & planningsassistent",
                        })}
                    />
                    <Bullet
                        icon={faCheckCircle}
                        title={languageSwitch(lang, {
                            EN: "Shared mailboxes",
                            ET: "Jagatud postkastid",
                            LT: "Bendrai naudojamos pašto dėžutės",
                            LV: "Koplietošanas pastkastītes",
                            ES: "Buzones compartidos",
                            TR: "Paylaşılan posta kutuları",
                            CZ: "Sdílené poštovní schránky",
                            EL: "Κοινόχρηστα γραμματοκιβώτια",
                            FR: "Boîtes aux lettres partagées",
                            NL: "Gedeelde mailboxen",
                        })}
                    />
                </div>
                {props.locale !== "FR-CA" && props.locale !== "EN-CA" && (
                    <>
                        <div className="bg-white/50 p-10 flex flex-col col-span-full">
                            <div className="font-bold text-3xl">{prices.commercial.title}</div>
                            <div className="font-semibold text-3xl text-gray-500 mb-5">
                                {prices.commercial.headline}
                            </div>
                            <div className="font-semibold text-sm text-black mb-5">{prices.commercial.description}</div>

                            <Bullet
                                icon={faCheckCircle}
                                title={languageSwitch(lang, {
                                    EN: "Advanced planning tool for large-scale projects",
                                    ET: "Täiustatud planeerimisvahend suuremahuliste projektide jaoks",
                                    LT: "Išplėstinė planavimo priemonė didelės apimties projektams",
                                    LV: "Uzlabots plānošanas rīks liela mēroga projektiem",
                                    ES: "Herramienta de planificación avanzada para proyectos a gran escala",
                                    TR: "Büyük ölçekli projeler için gelişmiş planlama aracı",
                                    CZ: "Pokročilý plánovací nástroj pro rozsáhlé projekty",
                                    EL: "Προηγμένο εργαλείο μελετών για έργα μεγάλης κλίμακας",
                                    FR: "Outil de planification avancé pour les projets à grande échelle",
                                    NL: "Geavanceerde planningstool voor grootschalige projecten",
                                })}
                            />
                            <Bullet
                                icon={faCheckCircle}
                                title={languageSwitch(lang, {
                                    EN: "Projects up to 5000 kWPeak",
                                    ET: "Projektid kuni 5000 kWPeak",
                                    LT: "Projektai iki 5000 kWPeak",
                                    LV: "Projekti līdz 5000 kWPeak",
                                    ES: "Proyectos de hasta 5000 kWPeak",
                                    TR: "5000 kWPeak'e kadar projeler",
                                    CZ: "Projekty až do 5000 kWPeak",
                                    EL: "Έργα έως 5000 kWPeak",
                                    FR: "Projets jusqu'à 5000 kWPeak",
                                    NL: "Projecten tot 5000 kWPiek",
                                })}
                            />
                            <Bullet
                                icon={faCheckCircle}
                                title={languageSwitch(lang, {
                                    EN: "Grid load analysis",
                                    ET: "Võrgukoormuse analüüs",
                                    LT: "Tinklo apkrovos analizė",
                                    LV: "Tīkla slodzes analīze",
                                    ES: "Análisis de la carga de la red",
                                    TR: "Şebeke yük analizi",
                                    CZ: "Analýza zatížení sítě",
                                    EL: "Ανάλυση φορτίου δικτύου",
                                    FR: "Analyse de la charge du réseau",
                                    NL: "Analyse netbelasting",
                                })}
                            />
                            <Bullet
                                icon={faCheckCircle}
                                title={languageSwitch(lang, {
                                    EN: "Custom load profiles",
                                    ET: "Kohandatud koormusprofiilid",
                                    LT: "Pasirinktiniai apkrovos profiliai",
                                    LV: "Pielāgotie slodzes profili",
                                    ES: "Perfiles de carga personalizados",
                                    TR: "Özel yük profilleri",
                                    CZ: "Vlastní profily zatížení",
                                    EL: "Προσαρμοσμένα προφίλ φορτίου",
                                    FR: "Profils de charge personnalisés",
                                    NL: "Aangepaste belastingsprofielen",
                                })}
                            />
                            <Bullet
                                icon={faCheckCircle}
                                title={languageSwitch(lang, {
                                    EN: "Simulate multiple producers & consumers",
                                    ET: "Simuleerida mitut tootjat ja tarbijat",
                                    LT: "Imituoti kelis gamintojus ir vartotojus",
                                    LV: "Simulēt vairākus ražotājus un patērētājus",
                                    ES: "Simulación de múltiples productores y consumidores",
                                    TR: "Birden fazla üretici ve tüketiciyi simüle edin",
                                    CZ: "Simulace více výrobců a spotřebitelů",
                                    EL: "Προσομοίωση πολλαπλών παραγωγών & καταναλωτών",
                                    FR: "Simuler plusieurs producteurs et consommateurs",
                                    NL: "Meerdere producenten en consumenten simuleren",
                                })}
                            />
                            <Bullet
                                icon={faCheckCircle}
                                title={languageSwitch(lang, {
                                    EN: "Project management & task tracking",
                                    ET: "Projektijuhtimine ja ülesannete jälgimine",
                                    LT: "Projektų valdymas ir užduočių stebėjimas",
                                    LV: "Projektu pārvaldība un uzdevumu izsekošana",
                                    ES: "Gestión de proyectos y seguimiento de tareas",
                                    TR: "Proje yönetimi ve görev takibi",
                                    CZ: "Řízení projektů a sledování úkolů",
                                    EL: "Διαχείριση έργων & παρακολούθηση εργασιών",
                                    FR: "Gestion de projet et suivi des tâches",
                                    NL: "Projectbeheer en taken bijhouden",
                                })}
                            />
                        </div>
                    </>
                )}
            </div>
        </Section>
    );
};
